/**
 * @fileoverview Job Settings Page
 *
 * Purpose: Manage job configuration and team via tabbed interface.
 * URL: /dashboard/jobs/:jobId/settings
 *
 * Each settings group on Job Details, Appointment, and Document Setup has
 * its own Save (not autosave) so edits are committed next to the fields
 * they belong to.
 *
 * Dependencies:
 * - Convex: jobs.queries.getJob, jobs.mutations.updateJob
 * - JobTeamPanel, JobSettingsSection
 */

import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import type { FunctionArgs } from "convex/server";
import { api } from "@cvx/_generated/api";
import type { Doc, Id } from "@cvx/_generated/dataModel";
import { AU_JURISDICTIONS } from "@cvx/lib/businessDays";
import { normaliseAdditionalPlacesOfBusiness } from "@cvx/lib/placesOfBusiness";
import { AU_STATES } from "@/utils/format/auStates";
import type {
  ActOfBankruptcyParagraph,
  AdditionalPlaceOfBusiness,
  AppointmentType,
  BankruptcyPetitionType,
  CompanyNameVariant,
  CompanyNameVariantType,
  JobRole,
  JobStatus,
  WindingUpApplicationStatus,
} from "@cvx/jobs/schema";
import { Button, buttonVariants } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Label } from "@/ui/primitives/label";
import { Textarea } from "@/ui/primitives/textarea";
import { Checkbox } from "@/ui/primitives/checkbox";
import { Badge } from "@/ui/primitives/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import {
  Loader2,
  X,
  FileCog,
  Users,
  Tag,
  FileText,
  Plus,
  Plug,
  Search,
  Clock,
  ShieldCheck,
  History as HistoryIcon,
  AlertCircle,
  Info,
  ScrollText,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useState, useEffect, useMemo, useRef, type FormEvent } from "react";
import { toast } from "sonner";
import { cn } from "@/utils/misc";
import { DatePicker } from "@/ui/composed/inputs/date-picker";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/ui/composed/overlays/alert-dialog";
import { useUnsavedChangesWarning } from "@/ui/hooks/use-unsaved-changes-warning";
import { JobTeamPanel } from "./-ui.job-team-panel";
import { JobSettingsSection } from "./-ui.job-settings-section";
import { JobActivityPanel } from "./-ui.job-activity-panel";
import { JobAuditLogPanel } from "./-ui.job-audit-log-panel";
import { JobIntegrationsPanel } from "./-ui.job-integrations-panel";
import { JobNoticeOfAppointmentPanel } from "./-ui.job-noa-panel";
import { JobConflictCheckPanel } from "./-ui.job-conflict-check-panel";
import { RelationBackFieldsSection } from "./-ui.job-relation-back-fields";
import {
  BankruptcyCaseRecordSection,
  type BankruptcyCaseRecordState,
} from "./-ui.job-bankruptcy-case-record";
import { RecordAppointmentChangeDialog } from "./-ui.record-appointment-change-dialog";
import { ANZSIC_CODES } from "@/data/anzsic-codes";
import { JOB_STATUS_OPTIONS } from "@/lib/job-statuses";
import {
  FIELD_CATEGORIES,
  appointmentTypeAppliesToField,
} from "@cvx/settings/precedents/canonicalFields";
import {
  isBankruptcyAppointmentType,
  isPersonalMatter,
} from "@cvx/lib/appointmentTypes";
import {
  PersonalIdentityFields,
  type PersonalIdentityValues,
} from "./-ui.job-personal-identity-fields";

export const Route = createFileRoute(
  "/_app/_auth/dashboard/_layout/jobs/$jobId/settings",
)({
  component: JobSettingsPage,
  // `?tab=appointment` deep-links straight to a settings tab (e.g. the
  // Review Windows panel links here when the winding-up application date
  // is missing).
  validateSearch: (search: Record<string, unknown>): { tab?: SettingsTab } => {
    const tab = search.tab;
    return SETTINGS_TABS.some((t) => t.key === tab)
      ? { tab: tab as SettingsTab }
      : {};
  },
});

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const APPOINTMENT_TYPE_LABELS: Record<string, string> = {
  cvl: "Creditors Voluntary Liquidation (CVL)",
  mvl: "Members Voluntary Liquidation (MVL)",
  simplified_liquidation: "Simplified Liquidation",
  court_liquidation: "Court Liquidation",
  provisional_liquidation: "Provisional Liquidation",
  sbr: "Small Business Restructuring (SBR)",
  voluntary_administration: "Voluntary Administration (VA)",
  deed_of_company_arrangement: "Deed of Company Arrangement (DOCA)",
  receivership: "Receivership",
  scheme_of_arrangement: "Scheme of Arrangement",
  personal_bankruptcy: "Personal Bankruptcy",
  business_bankruptcy: "Business Bankruptcy",
  creditors_petition: "Creditors Petition",
  debtors_petition: "Debtors Petition",
  part_x_arrangement: "Personal Insolvency Agreement (Part X)",
};

const COMPANY_NAME_VARIANT_TYPES: CompanyNameVariantType[] = [
  "trading_name",
  "trust_name",
  "former_trading_name",
];

const COMPANY_NAME_VARIANT_TYPE_LABELS: Record<CompanyNameVariantType, string> =
  {
    trading_name: "Trading name",
    trust_name: "Trust name",
    former_trading_name: "Former trading name",
  };

function normaliseCompanyNameVariants(
  variants: CompanyNameVariant[],
): CompanyNameVariant[] {
  const seen = new Set<string>();

  return variants.flatMap((variant) => {
    const name = variant.name.trim();
    if (!name) return [];

    const key = `${variant.type}:${name.toLowerCase()}`;
    if (seen.has(key)) return [];
    seen.add(key);

    return [{ ...variant, name }];
  });
}

type SettingsTab =
  | "details"
  | "team"
  | "tags"
  | "appointment"
  | "conflict"
  | "integrations"
  | "activity"
  | "audit"
  | "history";

const SETTINGS_TABS: ReadonlyArray<{
  key: SettingsTab;
  label: string;
  icon: LucideIcon;
}> = [
  { key: "details", label: "Job Details", icon: FileCog },
  { key: "appointment", label: "Appointment Details", icon: FileText },
  // The conflict poll is run pre-appointment but is part of the file
  // afterwards, so it shows on every job. Likely to move under an Audit
  // grouping once there are more audit surfaces.
  { key: "conflict", label: "Conflict Check", icon: ShieldCheck },
  { key: "team", label: "Team", icon: Users },
  { key: "tags", label: "Document Setup", icon: Tag },
  { key: "integrations", label: "Integrations", icon: Plug },
  { key: "activity", label: "Activity", icon: Clock },
  { key: "audit", label: "Audit Log", icon: ScrollText },
  { key: "history", label: "History", icon: HistoryIcon },
];

/**
 * Tabs that only make sense once the firm has actually been appointed.
 * A job still in the pipeline (or one whose pitch died) has no appointment
 * instrument, date, or court detail to show.
 */
const APPOINTED_ONLY_TABS: ReadonlyArray<SettingsTab> = ["appointment"];

/** Has this job been converted to an appointment yet? */
function isAppointedJob(status: JobStatus): boolean {
  return status !== "pre_appointment" && status !== "did_not_proceed";
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function JobSettingsPage() {
  const { jobId } = Route.useParams();
  const { tab: tabFromSearch } = Route.useSearch();
  const [activeTab, setActiveTab] = useState<SettingsTab>(
    tabFromSearch ?? "details",
  );
  const [detailsDirty, setDetailsDirty] = useState(false);
  const [appointmentDirty, setAppointmentDirty] = useState(false);
  const [documentSetupDirty, setDocumentSetupDirty] = useState(false);
  const [pendingTab, setPendingTab] = useState<SettingsTab | null>(null);

  // Honour deep-links arriving while the page is already mounted.
  useEffect(() => {
    if (tabFromSearch) setActiveTab(tabFromSearch);
  }, [tabFromSearch]);

  const { data: job, isLoading } = useQuery(
    convexQuery(api.jobs.queries.getJob, { jobId: jobId as Id<"jobs"> }),
  );

  const visibleTabs = useMemo(() => {
    if (job && !isAppointedJob(job.status as JobStatus)) {
      return SETTINGS_TABS.filter((t) => !APPOINTED_ONLY_TABS.includes(t.key));
    }
    return SETTINGS_TABS;
  }, [job]);

  // `?tab=appointment` is a valid search param but the tab may be hidden for
  // this job, so fall back rather than rendering a tab with nothing selected.
  const currentTab = visibleTabs.some((t) => t.key === activeTab)
    ? activeTab
    : "details";

  const currentTabIsDirty =
    (currentTab === "details" && detailsDirty) ||
    (currentTab === "appointment" && appointmentDirty) ||
    (currentTab === "tags" && documentSetupDirty);

  useUnsavedChangesWarning(currentTabIsDirty);

  const requestTabChange = (next: SettingsTab) => {
    if (next === currentTab) return;
    if (currentTabIsDirty) {
      setPendingTab(next);
      return;
    }
    setActiveTab(next);
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="flex h-64 items-center justify-center">
        <p className="text-sm text-muted-foreground">Job not found.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {/* Tabs */}
      <div className="border-b bg-background px-6">
        <nav className="-mb-px flex gap-1" aria-label="Settings sections">
          {visibleTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => requestTabChange(tab.key)}
              className={cn(
                "flex items-center gap-1.5 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors",
                currentTab === tab.key
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:border-muted-foreground/30 hover:text-foreground",
              )}
            >
              <tab.icon className="h-3.5 w-3.5" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div
        className={cn(
          "p-6",
          currentTab === "audit"
            ? "max-w-none"
            : currentTab === "appointment"
              ? "max-w-6xl"
              : "max-w-3xl",
        )}
      >
        {currentTab === "details" && (
          <JobSettingsForm job={job} onDirtyChange={setDetailsDirty} />
        )}
        {currentTab === "team" && (
          <JobTeamPanel
            jobId={job._id}
            orgId={job.orgId}
            userRole={job.userRole as JobRole}
          />
        )}
        {currentTab === "tags" && (
          <JobDocumentSetupPanel
            job={job}
            onDirtyChange={setDocumentSetupDirty}
          />
        )}
        {currentTab === "appointment" && (
          <JobAppointmentPanel job={job} onDirtyChange={setAppointmentDirty} />
        )}
        {currentTab === "conflict" && <JobConflictCheckPanel jobId={job._id} />}
        {currentTab === "integrations" && (
          <JobIntegrationsPanel jobId={job._id} />
        )}
        {currentTab === "activity" && <JobActivityPanel jobId={job._id} />}
        {currentTab === "audit" && <JobAuditLogPanel jobId={job._id} />}
        {currentTab === "history" && (
          <JobHistoryPanel
            jobId={job._id}
            isPreAppointment={job.status === "pre_appointment"}
            userRole={job.userRole as JobRole}
          />
        )}
      </div>

      <AlertDialog
        open={pendingTab !== null}
        onOpenChange={(open) => {
          if (!open) setPendingTab(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved changes</AlertDialogTitle>
            <AlertDialogDescription>
              This tab has unsaved edits. Save the section you were working on,
              or leave and discard those edits.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Stay on this tab</AlertDialogCancel>
            <AlertDialogAction
              className={buttonVariants({ variant: "outline" })}
              onClick={() => {
                if (pendingTab) setActiveTab(pendingTab);
                setPendingTab(null);
              }}
            >
              Leave without saving
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Settings Form
// ---------------------------------------------------------------------------

interface JobData {
  _id: Id<"jobs">;
  orgId: Id<"organizations">;
  jobName?: string;
  jobCode?: string;
  entityName: string;
  acn?: string;
  abn?: string;
  // MOR-340 trust handling.
  trustAbn?: string;
  trustName?: string;
  trusteeCapacity?: "sole_trustee" | "dual_capacity" | "unknown";
  companyNameVariants?: CompanyNameVariant[];
  anzsicCode?: string;
  registeredOfficeStreet?: string;
  registeredOfficeSuburb?: string;
  registeredOfficeState?: string;
  registeredOfficePostcode?: string;
  principalPlaceStreet?: string;
  principalPlaceSuburb?: string;
  principalPlaceState?: string;
  principalPlacePostcode?: string;
  // MOR-989 — manually entered trading addresses.
  additionalPlacesOfBusiness?: AdditionalPlaceOfBusiness[];
  appointmentType?: string;
  appointmentDate?: string;
  // MOR-901 — appointment history, most recent last.
  priorAppointments?: Array<{
    appointmentType: string;
    appointmentDate?: string;
    changedAt: number;
    changedByUserId?: Id<"users">;
    note?: string;
  }>;
  // FEG case-level dates
  administrationDate?: string;
  liquidationDate?: string;
  administrationContinuous?: boolean;
  appointeeJurisdiction?: string;
  statutoryTasksMode?: "inherit" | "on" | "off";
  status: string;
  practitioners: string[];
  notes?: string;
  userRole: string;
  customTagSelections?: Record<string, string[]>;
  variantSelections?: Record<string, string>;
  // MOR-429 day-one fields
  courtName?: string;
  courtState?: string;
  courtNumber?: string;
  sequestrationNumber?: string;
  proceedingMatterNumber?: string;
  proceedingYear?: string;
  courtOrderDate?: string;
  windingUpApplicationDate?: string;
  administrationStartDate?: string;
  // MOR-901 — prior winding-up application + relation-back override
  windingUpApplicationStatus?: WindingUpApplicationStatus;
  relationBackDateOverride?: string;
  plaintiffName?: string;
  defendantName?: string;
  bankruptName?: string;
  bankruptDateOfBirth?: string;
  administrationNumber?: string;
  petitioningCreditorCosts?: string;
  actOfBankruptcyDate?: string;
  // MOR-1252 — bankruptcy case record
  bankruptcyPetitionType?: BankruptcyPetitionType;
  petitionPresentedDate?: string;
  dateOfBankruptcy?: string;
  actOfBankruptcyParagraph?: ActOfBankruptcyParagraph;
  actOfBankruptcyDescription?: string;
  creditorsPetitionPending?: boolean;
  courtSpecifiedCommencementDate?: string;
  // MOR-1255 — individual insolvency inputs and the business guard.
  bankruptCarriedOnBusiness?: boolean;
  earliestUnpaidJudgmentDate?: string;
  statementOfAffairsInsolvencyDate?: string;
  bankruptcyNoticeExtensionDisregarded?: boolean;
  // MOR-770 — job bank account (manual entry, pulled through as
  // `job_bank_*` canonical fields at document generation).
  bankName?: string;
  bankAccountName?: string;
  bankBsb?: string;
  bankAccountNumber?: string;
}

type UpdateJobFields = Omit<
  FunctionArgs<typeof api.jobs.mutations.updateJob>,
  "jobId"
>;

type DetailsSection =
  | "identity"
  | "names"
  | "industry"
  | "registeredOffice"
  | "principalPlace"
  | "additionalPlaces"
  | "bank"
  | "status";

function JobSettingsForm({
  job,
  onDirtyChange,
}: {
  job: JobData;
  onDirtyChange: (dirty: boolean) => void;
}) {
  const [jobName, setJobName] = useState(job.jobName ?? "");
  const [jobCode, setJobCode] = useState(job.jobCode ?? "");
  const [entityName, setEntityName] = useState(job.entityName);
  const [acn, setAcn] = useState(job.acn ?? "");
  const [abn, setAbn] = useState(job.abn ?? "");
  // MOR-1858 (QA-07) — a personal insolvency job identifies an individual,
  // not a company: the identity section edits the bankrupt's name and date
  // of birth here instead of the ACN/ABN/trust fields. A pre-appointment
  // personal job has no Appointment tab, so this is its only editor.
  const isPersonal = isPersonalMatter(job);
  const [personalIdentity, setPersonalIdentity] =
    useState<PersonalIdentityValues>({
      bankruptName: job.bankruptName ?? "",
      bankruptDateOfBirth: job.bankruptDateOfBirth ?? "",
    });
  // MOR-340 — trust fields. `isTrustee` mirrors the same toggle on the
  // create-job dialog: when true, we show the trust ABN/name inputs and
  // recompute `trusteeCapacity` from whichever ABNs end up populated.
  const [isTrustee, setIsTrustee] = useState(
    Boolean(job.trustAbn || job.trustName),
  );
  const [trustAbn, setTrustAbn] = useState(job.trustAbn ?? "");
  const [trustName, setTrustName] = useState(job.trustName ?? "");
  const [companyNameVariants, setCompanyNameVariants] = useState<
    CompanyNameVariant[]
  >(job.companyNameVariants ?? []);
  const [anzsicCode, setAnzsicCode] = useState(job.anzsicCode ?? "");
  const [anzsicSearch, setAnzsicSearch] = useState("");
  const [anzsicDropdownOpen, setAnzsicDropdownOpen] = useState(false);
  // Tracks the last server-adopted ANZSIC code so the sync effect below can
  // tell "the job changed elsewhere" apart from "this section's code was
  // actually adopted" — only the latter should clear a typed search term.
  const adoptedAnzsicCodeRef = useRef(job.anzsicCode ?? "");
  const [registeredOfficeStreet, setRegisteredOfficeStreet] = useState(
    job.registeredOfficeStreet ?? "",
  );
  const [registeredOfficeSuburb, setRegisteredOfficeSuburb] = useState(
    job.registeredOfficeSuburb ?? "",
  );
  const [registeredOfficeState, setRegisteredOfficeState] = useState(
    job.registeredOfficeState ?? "",
  );
  const [registeredOfficePostcode, setRegisteredOfficePostcode] = useState(
    job.registeredOfficePostcode ?? "",
  );
  const [principalPlaceStreet, setPrincipalPlaceStreet] = useState(
    job.principalPlaceStreet ?? "",
  );
  const [principalPlaceSuburb, setPrincipalPlaceSuburb] = useState(
    job.principalPlaceSuburb ?? "",
  );
  const [principalPlaceState, setPrincipalPlaceState] = useState(
    job.principalPlaceState ?? "",
  );
  const [principalPlacePostcode, setPrincipalPlacePostcode] = useState(
    job.principalPlacePostcode ?? "",
  );
  const [additionalPlacesOfBusiness, setAdditionalPlacesOfBusiness] = useState<
    AdditionalPlaceOfBusiness[]
  >(job.additionalPlacesOfBusiness ?? []);
  // MOR-770 — job bank account. Manually entered here so documents can pull
  // the estate account through as `job_bank_*` fields.
  const [bankName, setBankName] = useState(job.bankName ?? "");
  const [bankAccountName, setBankAccountName] = useState(
    job.bankAccountName ?? "",
  );
  const [bankBsb, setBankBsb] = useState(job.bankBsb ?? "");
  const [bankAccountNumber, setBankAccountNumber] = useState(
    job.bankAccountNumber ?? "",
  );
  const [status, setStatus] = useState(job.status);
  const [savingSection, setSavingSection] = useState<DetailsSection | null>(
    null,
  );

  const { mutateAsync: updateJob } = useMutation({
    mutationFn: useConvexMutation(api.jobs.mutations.updateJob),
  });

  const isOwnerOrMember = job.userRole === "owner" || job.userRole === "member";

  // MOR-340 — derive trustee capacity from the live form state. Mirrors
  // the same rule used at job creation: `sole_trustee` when only the
  // trust ABN is filled, `dual_capacity` when both ABNs are filled.
  const trusteeCapacity: "sole_trustee" | "dual_capacity" | undefined = (() => {
    if (!isTrustee || !trustAbn.trim()) return undefined;
    return abn.trim() ? "dual_capacity" : "sole_trustee";
  })();
  const showDualCapacityWarning = trusteeCapacity === "dual_capacity";

  const identityHasChanges =
    jobName !== (job.jobName ?? "") ||
    jobCode !== (job.jobCode ?? "") ||
    entityName !== job.entityName ||
    acn !== (job.acn ?? "") ||
    abn !== (job.abn ?? "") ||
    (isTrustee ? trustAbn : "") !== (job.trustAbn ?? "") ||
    (isTrustee ? trustName : "") !== (job.trustName ?? "") ||
    (trusteeCapacity ?? null) !== (job.trusteeCapacity ?? null) ||
    personalIdentity.bankruptName !== (job.bankruptName ?? "") ||
    personalIdentity.bankruptDateOfBirth !== (job.bankruptDateOfBirth ?? "");
  const namesHasChanges =
    JSON.stringify(normaliseCompanyNameVariants(companyNameVariants)) !==
    JSON.stringify(job.companyNameVariants ?? []);
  const industryHasChanges = anzsicCode !== (job.anzsicCode ?? "");
  const registeredOfficeHasChanges =
    registeredOfficeStreet !== (job.registeredOfficeStreet ?? "") ||
    registeredOfficeSuburb !== (job.registeredOfficeSuburb ?? "") ||
    registeredOfficeState !== (job.registeredOfficeState ?? "") ||
    registeredOfficePostcode !== (job.registeredOfficePostcode ?? "");
  const principalPlaceHasChanges =
    principalPlaceStreet !== (job.principalPlaceStreet ?? "") ||
    principalPlaceSuburb !== (job.principalPlaceSuburb ?? "") ||
    principalPlaceState !== (job.principalPlaceState ?? "") ||
    principalPlacePostcode !== (job.principalPlacePostcode ?? "");
  const additionalPlacesHasChanges =
    JSON.stringify(
      normaliseAdditionalPlacesOfBusiness(additionalPlacesOfBusiness),
    ) !==
    JSON.stringify(
      normaliseAdditionalPlacesOfBusiness(job.additionalPlacesOfBusiness ?? []),
    );
  const bankHasChanges =
    bankName !== (job.bankName ?? "") ||
    bankAccountName !== (job.bankAccountName ?? "") ||
    bankBsb !== (job.bankBsb ?? "") ||
    bankAccountNumber !== (job.bankAccountNumber ?? "");
  const statusHasChanges = status !== job.status;
  const hasChanges =
    identityHasChanges ||
    namesHasChanges ||
    industryHasChanges ||
    registeredOfficeHasChanges ||
    principalPlaceHasChanges ||
    additionalPlacesHasChanges ||
    bankHasChanges ||
    statusHasChanges;

  useEffect(() => {
    onDirtyChange(hasChanges);
    return () => onDirtyChange(false);
  }, [hasChanges, onDirtyChange]);

  // Adopt server values for clean sections only, so saving one group does
  // not wipe unsaved edits in another.
  useEffect(() => {
    if (!identityHasChanges) {
      setJobName(job.jobName ?? "");
      setJobCode(job.jobCode ?? "");
      setEntityName(job.entityName);
      setAcn(job.acn ?? "");
      setAbn(job.abn ?? "");
      setTrustAbn(job.trustAbn ?? "");
      setTrustName(job.trustName ?? "");
      setPersonalIdentity({
        bankruptName: job.bankruptName ?? "",
        bankruptDateOfBirth: job.bankruptDateOfBirth ?? "",
      });
      // `isTrustee` is UI-only (derived once, then toggled locally) — never
      // stomp a practitioner's just-ticked toggle just because some other
      // section's save re-ran this effect. Only turn it on to reflect a
      // server-adopted trust; never turn it back off from here.
      setIsTrustee((prev) => prev || Boolean(job.trustAbn || job.trustName));
    }
    if (!namesHasChanges) {
      setCompanyNameVariants(job.companyNameVariants ?? []);
    }
    if (!industryHasChanges) {
      setAnzsicCode(job.anzsicCode ?? "");
      // Only clear a typed-but-unselected search term when the adopted code
      // actually changed (a real save), not on every unrelated job update.
      if (adoptedAnzsicCodeRef.current !== (job.anzsicCode ?? "")) {
        setAnzsicSearch("");
      }
    }
    adoptedAnzsicCodeRef.current = job.anzsicCode ?? "";
    if (!registeredOfficeHasChanges) {
      setRegisteredOfficeStreet(job.registeredOfficeStreet ?? "");
      setRegisteredOfficeSuburb(job.registeredOfficeSuburb ?? "");
      setRegisteredOfficeState(job.registeredOfficeState ?? "");
      setRegisteredOfficePostcode(job.registeredOfficePostcode ?? "");
    }
    if (!principalPlaceHasChanges) {
      setPrincipalPlaceStreet(job.principalPlaceStreet ?? "");
      setPrincipalPlaceSuburb(job.principalPlaceSuburb ?? "");
      setPrincipalPlaceState(job.principalPlaceState ?? "");
      setPrincipalPlacePostcode(job.principalPlacePostcode ?? "");
    }
    if (!additionalPlacesHasChanges) {
      setAdditionalPlacesOfBusiness(job.additionalPlacesOfBusiness ?? []);
    }
    if (!bankHasChanges) {
      setBankName(job.bankName ?? "");
      setBankAccountName(job.bankAccountName ?? "");
      setBankBsb(job.bankBsb ?? "");
      setBankAccountNumber(job.bankAccountNumber ?? "");
    }
    if (!statusHasChanges) {
      setStatus(job.status);
    }
  }, [
    job,
    identityHasChanges,
    namesHasChanges,
    industryHasChanges,
    registeredOfficeHasChanges,
    principalPlaceHasChanges,
    additionalPlacesHasChanges,
    bankHasChanges,
    statusHasChanges,
  ]);

  const handleAddCompanyNameVariant = () => {
    setCompanyNameVariants((prev) => [
      ...prev,
      {
        name: "",
        type: "trading_name",
        includeInDocuments: true,
        source: "manual",
      },
    ]);
  };

  const handleUpdateCompanyNameVariant = (
    index: number,
    updates: Partial<CompanyNameVariant>,
  ) => {
    setCompanyNameVariants((prev) =>
      prev.map((variant, i) =>
        i === index ? { ...variant, ...updates } : variant,
      ),
    );
  };

  const handleRemoveCompanyNameVariant = (index: number) => {
    setCompanyNameVariants((prev) => prev.filter((_, i) => i !== index));
  };

  // MOR-989 — additional places of business, edited as a list of rows.
  const handleAddAdditionalPlace = () => {
    setAdditionalPlacesOfBusiness((prev) => [
      ...prev,
      {
        street: "",
        suburb: "",
        state: "",
        postcode: "",
        includeInDocuments: true,
        source: "manual",
      },
    ]);
  };

  const handleUpdateAdditionalPlace = (
    index: number,
    updates: Partial<AdditionalPlaceOfBusiness>,
  ) => {
    setAdditionalPlacesOfBusiness((prev) =>
      prev.map((place, i) => (i === index ? { ...place, ...updates } : place)),
    );
  };

  const handleRemoveAdditionalPlace = (index: number) => {
    setAdditionalPlacesOfBusiness((prev) => prev.filter((_, i) => i !== index));
  };

  const saveSection = async (
    section: DetailsSection,
    payload: UpdateJobFields,
    successMessage: string,
  ) => {
    setSavingSection(section);
    try {
      await updateJob({ jobId: job._id, ...payload });
      toast.success(successMessage);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to update job";
      toast.error(message);
    } finally {
      setSavingSection(null);
    }
  };

  const handleSaveIdentity = () =>
    saveSection(
      "identity",
      {
        jobName: jobName.trim() || undefined,
        jobCode: jobCode.trim() || undefined,
        entityName,
        acn: acn.trim() || undefined,
        abn: abn.trim() || undefined,
        trustAbn: isTrustee && trustAbn.trim() ? trustAbn.trim() : undefined,
        trustName: isTrustee && trustName.trim() ? trustName.trim() : undefined,
        trusteeCapacity,
        bankruptName: personalIdentity.bankruptName.trim() || undefined,
        // "" clears a recorded date of birth; an empty field on a job that
        // never had one is left out so the save doesn't log a no-op change.
        bankruptDateOfBirth:
          personalIdentity.bankruptDateOfBirth.trim() ||
          (job.bankruptDateOfBirth ? "" : undefined),
      },
      "Job identity updated",
    );

  const handleDiscardIdentity = () => {
    setJobName(job.jobName ?? "");
    setJobCode(job.jobCode ?? "");
    setEntityName(job.entityName);
    setAcn(job.acn ?? "");
    setAbn(job.abn ?? "");
    setIsTrustee(Boolean(job.trustAbn || job.trustName));
    setTrustAbn(job.trustAbn ?? "");
    setTrustName(job.trustName ?? "");
    setPersonalIdentity({
      bankruptName: job.bankruptName ?? "",
      bankruptDateOfBirth: job.bankruptDateOfBirth ?? "",
    });
  };

  const handleSaveNames = () =>
    saveSection(
      "names",
      {
        companyNameVariants: normaliseCompanyNameVariants(companyNameVariants),
      },
      "Additional company names updated",
    );

  const handleSaveIndustry = () =>
    saveSection(
      "industry",
      { anzsicCode: anzsicCode.trim() || undefined },
      "Industry updated",
    );

  const handleSaveRegisteredOffice = () => {
    const fullRegisteredAddress = [
      registeredOfficeStreet.trim(),
      registeredOfficeSuburb.trim(),
      registeredOfficeState.trim(),
      registeredOfficePostcode.trim(),
    ]
      .filter(Boolean)
      .join(", ");
    return saveSection(
      "registeredOffice",
      {
        registeredAddress: fullRegisteredAddress || undefined,
        registeredOfficeStreet: registeredOfficeStreet.trim() || undefined,
        registeredOfficeSuburb: registeredOfficeSuburb.trim() || undefined,
        registeredOfficeState: registeredOfficeState.trim() || undefined,
        registeredOfficePostcode: registeredOfficePostcode.trim() || undefined,
      },
      "Registered office updated",
    );
  };

  const handleSavePrincipalPlace = () =>
    saveSection(
      "principalPlace",
      {
        principalPlaceStreet: principalPlaceStreet.trim() || undefined,
        principalPlaceSuburb: principalPlaceSuburb.trim() || undefined,
        principalPlaceState: principalPlaceState.trim() || undefined,
        principalPlacePostcode: principalPlacePostcode.trim() || undefined,
      },
      "Principal place of business updated",
    );

  const handleSaveAdditionalPlaces = () =>
    saveSection(
      "additionalPlaces",
      {
        additionalPlacesOfBusiness: normaliseAdditionalPlacesOfBusiness(
          additionalPlacesOfBusiness,
        ),
      },
      "Trading addresses updated",
    );

  const handleSaveBank = () =>
    saveSection(
      "bank",
      {
        bankName: bankName.trim() || undefined,
        bankAccountName: bankAccountName.trim() || undefined,
        bankBsb: bankBsb.trim() || undefined,
        bankAccountNumber: bankAccountNumber.trim() || undefined,
      },
      "Bank details updated",
    );

  const handleSaveStatus = () =>
    saveSection("status", { status: status as JobStatus }, "Status updated");

  return (
    <div className="flex flex-col gap-6">
      <JobSettingsSection
        title="Job identity"
        description={
          isPersonal
            ? "Name of the estate and the individual whose affairs it covers."
            : "Name, identifiers, and trustee details for this job."
        }
        canEdit={isOwnerOrMember}
        isDirty={identityHasChanges}
        isSaving={savingSection === "identity"}
        onSave={() => {
          void handleSaveIdentity();
        }}
        onDiscard={handleDiscardIdentity}
      >
        {/* Job Name */}
        <div className="flex flex-col gap-2">
          <Label htmlFor="settingsJobName">Job Name</Label>
          <Input
            id="settingsJobName"
            value={jobName}
            onChange={(e) => setJobName(e.target.value)}
            placeholder={job.entityName}
            disabled={!isOwnerOrMember}
          />
          <p className="text-xs text-muted-foreground">
            Short internal title for this job. Defaults to the entity name if
            left blank.
          </p>
        </div>

        {/* Job Code */}
        <div className="flex flex-col gap-2">
          <Label htmlFor="settingsJobCode">Job Code</Label>
          <Input
            id="settingsJobCode"
            value={jobCode}
            onChange={(e) => setJobCode(e.target.value)}
            placeholder={job.entityName}
            maxLength={50}
            disabled={!isOwnerOrMember}
          />
          <p className="text-xs text-muted-foreground">
            Sent to InfoTrack as the client reference for cost reconciliation.
          </p>
        </div>

        {/* Entity Name */}
        <div className="flex flex-col gap-2">
          <Label htmlFor="settingsEntityName">
            {!isPersonal
              ? "Entity Name"
              : job.appointmentType === "part_x_arrangement"
                ? "Debtor Estate Name"
                : "Bankrupt Estate Name"}
          </Label>
          <Input
            id="settingsEntityName"
            value={entityName}
            onChange={(e) => setEntityName(e.target.value)}
            disabled={!isOwnerOrMember}
          />
        </div>

        {/* MOR-1858 (QA-07) — the individual's identity replaces the
            company identifiers and trust block on a personal job. */}
        {isPersonal && (
          <PersonalIdentityFields
            values={personalIdentity}
            onChange={setPersonalIdentity}
            disabled={!isOwnerOrMember}
          />
        )}

        {/* ACN / ABN */}
        {!isPersonal && (
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="settingsAcn">ACN</Label>
              <Input
                id="settingsAcn"
                placeholder="000 000 000"
                value={acn}
                onChange={(e) => setAcn(e.target.value)}
                disabled={!isOwnerOrMember}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="settingsAbn">ABN</Label>
              <Input
                id="settingsAbn"
                placeholder="00 000 000 000"
                value={abn}
                onChange={(e) => setAbn(e.target.value)}
                disabled={!isOwnerOrMember}
              />
            </div>
          </div>
        )}

        {/* MOR-340 — Trust details. Same toggle pattern as the create-job
          dialog so the user can confirm trustee status, capture the trust
          ABN/name, and see the dual-capacity warning when both ABNs are
          present. `trusteeCapacity` is derived from filled fields above. */}
        {!isPersonal && (
          <div className="flex flex-col gap-3 rounded-md border border-dashed border-muted-foreground/30 p-3">
            <label className="flex items-start gap-2 text-sm">
              <Checkbox
                checked={isTrustee}
                onCheckedChange={(checked) => {
                  const next = checked === true;
                  setIsTrustee(next);
                  if (!next) {
                    setTrustAbn("");
                    setTrustName("");
                  }
                }}
                disabled={!isOwnerOrMember}
              />
              <span className="flex flex-col gap-0.5">
                <span className="font-medium">
                  This company is trustee of a trust
                </span>
                <span className="text-xs text-muted-foreground">
                  Capture the trust&apos;s ABN separately. If the company only
                  acts as trustee, leave the company ABN above blank.
                </span>
              </span>
            </label>

            {isTrustee && (
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="settingsTrustAbn">Trust ABN</Label>
                  <Input
                    id="settingsTrustAbn"
                    placeholder="00 000 000 000"
                    value={trustAbn}
                    onChange={(e) => setTrustAbn(e.target.value)}
                    disabled={!isOwnerOrMember}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="settingsTrustName">Trust name</Label>
                  <Input
                    id="settingsTrustName"
                    placeholder="e.g. The Smith Family Trust"
                    value={trustName}
                    onChange={(e) => setTrustName(e.target.value)}
                    disabled={!isOwnerOrMember}
                  />
                </div>
              </div>
            )}

            {isTrustee && trustAbn.trim() && (
              <p className="text-xs text-muted-foreground">
                Trustee capacity:{" "}
                <span className="font-medium text-foreground">
                  {trusteeCapacity === "dual_capacity"
                    ? "Dual capacity (company + trustee)"
                    : "Sole trustee"}
                </span>
              </p>
            )}

            {showDualCapacityWarning && (
              <div className="flex items-start gap-2 rounded-md bg-amber-50 p-2 text-xs text-amber-900 dark:bg-amber-900/20 dark:text-amber-200">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>
                  <strong>Dual capacity:</strong> the company has its own ABN
                  and is also trustee of a trust. It may have traded in both
                  capacities — investigation should consider both entities.
                </span>
              </div>
            )}
          </div>
        )}
      </JobSettingsSection>

      <JobSettingsSection
        title="Additional company names"
        description="Trading, trust, or former trading names. Tick which ones appear in generated documents."
        canEdit={isOwnerOrMember}
        isDirty={namesHasChanges}
        isSaving={savingSection === "names"}
        onSave={() => {
          void handleSaveNames();
        }}
        onDiscard={() => setCompanyNameVariants(job.companyNameVariants ?? [])}
        actions={
          isOwnerOrMember ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAddCompanyNameVariant}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add name
            </Button>
          ) : null
        }
      >
        {companyNameVariants.length > 0 ? (
          <div className="flex flex-col gap-3">
            {companyNameVariants.map((variant, index) => (
              <div
                key={`${variant.source}-${variant.type}-${index}`}
                className="grid gap-3 rounded-md border bg-background p-3 md:grid-cols-[minmax(0,1fr)_180px_auto]"
              >
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center gap-2">
                    <Input
                      aria-label="Additional company name"
                      value={variant.name}
                      disabled={!isOwnerOrMember}
                      onChange={(e) =>
                        handleUpdateCompanyNameVariant(index, {
                          name: e.target.value,
                        })
                      }
                      placeholder="Enter additional company name"
                    />
                    {variant.source === "abr" && (
                      <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">
                        ABR
                      </span>
                    )}
                  </div>
                  <label className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Checkbox
                      checked={variant.includeInDocuments}
                      disabled={!isOwnerOrMember}
                      onCheckedChange={(checked) =>
                        handleUpdateCompanyNameVariant(index, {
                          includeInDocuments: checked === true,
                        })
                      }
                    />
                    Include in generated documents
                  </label>
                </div>

                <Select
                  value={variant.type}
                  onValueChange={(value) =>
                    handleUpdateCompanyNameVariant(index, {
                      type: value as CompanyNameVariantType,
                    })
                  }
                  disabled={!isOwnerOrMember}
                >
                  <SelectTrigger aria-label="Name type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COMPANY_NAME_VARIANT_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>
                        {COMPANY_NAME_VARIANT_TYPE_LABELS[type]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {isOwnerOrMember && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="justify-self-end"
                    onClick={() => handleRemoveCompanyNameVariant(index)}
                    aria-label="Remove additional company name"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            No additional company names have been added yet.
          </p>
        )}
      </JobSettingsSection>

      <JobSettingsSection
        title="Industry"
        description="Used to pre-fill the Gallagher business/industry field."
        canEdit={isOwnerOrMember}
        isDirty={industryHasChanges}
        isSaving={savingSection === "industry"}
        onSave={() => {
          void handleSaveIndustry();
        }}
        onDiscard={() => {
          setAnzsicCode(job.anzsicCode ?? "");
          setAnzsicSearch("");
        }}
      >
        {/* ANZSIC Code */}
        <div className="flex flex-col gap-2">
          <Label>ANZSIC Code</Label>
          <div className="relative">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by code or industry name"
                className="pl-9"
                value={
                  anzsicSearch ||
                  (anzsicCode
                    ? (() => {
                        const match = ANZSIC_CODES.find(
                          (code) => code.code === anzsicCode,
                        );
                        return match
                          ? `${match.code} — ${match.description}`
                          : anzsicCode;
                      })()
                    : "")
                }
                onChange={(event) => {
                  setAnzsicSearch(event.target.value);
                  setAnzsicCode("");
                  setAnzsicDropdownOpen(true);
                }}
                onFocus={() => {
                  if (isOwnerOrMember && !anzsicCode)
                    setAnzsicDropdownOpen(true);
                }}
                onBlur={() => {
                  setTimeout(() => setAnzsicDropdownOpen(false), 200);
                }}
                disabled={!isOwnerOrMember}
              />
              {isOwnerOrMember && anzsicCode && (
                <button
                  type="button"
                  onClick={() => {
                    setAnzsicCode("");
                    setAnzsicSearch("");
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-muted-foreground hover:text-foreground"
                  aria-label="Clear ANZSIC code"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>

            {isOwnerOrMember && anzsicDropdownOpen && (
              <div className="absolute z-50 mt-1 max-h-60 w-full overflow-y-auto rounded-md border bg-popover p-1 shadow-md">
                {(() => {
                  const query = anzsicSearch.toLowerCase();
                  const filtered = query
                    ? ANZSIC_CODES.filter(
                        (code) =>
                          code.code.includes(query) ||
                          code.description.toLowerCase().includes(query) ||
                          code.divisionName.toLowerCase().includes(query),
                      )
                    : ANZSIC_CODES;
                  if (filtered.length === 0) {
                    return (
                      <p className="px-3 py-2 text-sm text-muted-foreground">
                        No matching codes
                      </p>
                    );
                  }
                  return filtered.slice(0, 50).map((code) => (
                    <button
                      key={code.code}
                      type="button"
                      className="flex w-full items-start gap-3 rounded-sm px-3 py-2 text-left hover:bg-muted"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => {
                        setAnzsicCode(code.code);
                        setAnzsicSearch("");
                        setAnzsicDropdownOpen(false);
                      }}
                    >
                      <span className="shrink-0 font-mono text-sm text-muted-foreground">
                        {code.code}
                      </span>
                      <div className="flex flex-col gap-0.5 overflow-hidden">
                        <span className="truncate text-sm">
                          {code.description}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {code.division} — {code.divisionName}
                        </span>
                      </div>
                    </button>
                  ));
                })()}
              </div>
            )}
          </div>
        </div>
      </JobSettingsSection>

      <JobSettingsSection
        title={isPersonal ? "Bankrupt's address" : "Registered office"}
        description={
          isPersonal
            ? "The residential address shown on the conflict check."
            : undefined
        }
        canEdit={isOwnerOrMember}
        isDirty={registeredOfficeHasChanges}
        isSaving={savingSection === "registeredOffice"}
        onSave={() => {
          void handleSaveRegisteredOffice();
        }}
        onDiscard={() => {
          setRegisteredOfficeStreet(job.registeredOfficeStreet ?? "");
          setRegisteredOfficeSuburb(job.registeredOfficeSuburb ?? "");
          setRegisteredOfficeState(job.registeredOfficeState ?? "");
          setRegisteredOfficePostcode(job.registeredOfficePostcode ?? "");
        }}
      >
        <div className="grid grid-cols-1 gap-3">
          <Input
            placeholder="Street address"
            value={registeredOfficeStreet}
            onChange={(e) => setRegisteredOfficeStreet(e.target.value)}
            disabled={!isOwnerOrMember}
          />
          <div className="grid grid-cols-3 gap-3">
            <Input
              placeholder="Suburb"
              value={registeredOfficeSuburb}
              onChange={(e) => setRegisteredOfficeSuburb(e.target.value)}
              disabled={!isOwnerOrMember}
            />
            <Input
              placeholder="State"
              value={registeredOfficeState}
              onChange={(e) => setRegisteredOfficeState(e.target.value)}
              disabled={!isOwnerOrMember}
            />
            <Input
              placeholder="Postcode"
              value={registeredOfficePostcode}
              onChange={(e) => setRegisteredOfficePostcode(e.target.value)}
              disabled={!isOwnerOrMember}
            />
          </div>
        </div>
      </JobSettingsSection>

      <JobSettingsSection
        title="Principal place of business"
        canEdit={isOwnerOrMember}
        isDirty={principalPlaceHasChanges}
        isSaving={savingSection === "principalPlace"}
        onSave={() => {
          void handleSavePrincipalPlace();
        }}
        onDiscard={() => {
          setPrincipalPlaceStreet(job.principalPlaceStreet ?? "");
          setPrincipalPlaceSuburb(job.principalPlaceSuburb ?? "");
          setPrincipalPlaceState(job.principalPlaceState ?? "");
          setPrincipalPlacePostcode(job.principalPlacePostcode ?? "");
        }}
      >
        <div className="grid grid-cols-1 gap-3">
          <Input
            placeholder="Street address"
            value={principalPlaceStreet}
            onChange={(e) => setPrincipalPlaceStreet(e.target.value)}
            disabled={!isOwnerOrMember}
          />
          <div className="grid grid-cols-3 gap-3">
            <Input
              placeholder="Suburb"
              value={principalPlaceSuburb}
              onChange={(e) => setPrincipalPlaceSuburb(e.target.value)}
              disabled={!isOwnerOrMember}
            />
            <Input
              placeholder="State"
              value={principalPlaceState}
              onChange={(e) => setPrincipalPlaceState(e.target.value)}
              disabled={!isOwnerOrMember}
            />
            <Input
              placeholder="Postcode"
              value={principalPlacePostcode}
              onChange={(e) => setPrincipalPlacePostcode(e.target.value)}
              disabled={!isOwnerOrMember}
            />
          </div>
        </div>
      </JobSettingsSection>

      <JobSettingsSection
        title="Additional places of business"
        description="Trading addresses not recorded on the company search. Each one is added to the recommended searches, the conflict check, and (when ticked) generated documents."
        canEdit={isOwnerOrMember}
        isDirty={additionalPlacesHasChanges}
        isSaving={savingSection === "additionalPlaces"}
        onSave={() => {
          void handleSaveAdditionalPlaces();
        }}
        onDiscard={() =>
          setAdditionalPlacesOfBusiness(job.additionalPlacesOfBusiness ?? [])
        }
        actions={
          isOwnerOrMember ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAddAdditionalPlace}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add address
            </Button>
          ) : null
        }
      >
        {additionalPlacesOfBusiness.length > 0 ? (
          <div className="flex flex-col gap-3">
            {additionalPlacesOfBusiness.map((place, index) => (
              <div
                key={`${place.source}-${index}`}
                className="grid gap-3 rounded-md border bg-background p-3 md:grid-cols-[minmax(0,1fr)_auto]"
              >
                <div className="flex flex-col gap-3">
                  <Input
                    aria-label="Additional place of business street"
                    placeholder="Street address"
                    value={place.street}
                    disabled={!isOwnerOrMember}
                    onChange={(e) =>
                      handleUpdateAdditionalPlace(index, {
                        street: e.target.value,
                      })
                    }
                  />
                  <div className="grid grid-cols-3 gap-3">
                    <Input
                      aria-label="Additional place of business suburb"
                      placeholder="Suburb"
                      value={place.suburb}
                      disabled={!isOwnerOrMember}
                      onChange={(e) =>
                        handleUpdateAdditionalPlace(index, {
                          suburb: e.target.value,
                        })
                      }
                    />
                    <Select
                      value={place.state}
                      onValueChange={(value) =>
                        handleUpdateAdditionalPlace(index, { state: value })
                      }
                      disabled={!isOwnerOrMember}
                    >
                      <SelectTrigger aria-label="Additional place of business state">
                        <SelectValue placeholder="State" />
                      </SelectTrigger>
                      <SelectContent>
                        {AU_STATES.map((state) => (
                          <SelectItem key={state.value} value={state.value}>
                            {state.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      aria-label="Additional place of business postcode"
                      placeholder="Postcode"
                      value={place.postcode}
                      disabled={!isOwnerOrMember}
                      onChange={(e) =>
                        handleUpdateAdditionalPlace(index, {
                          postcode: e.target.value,
                        })
                      }
                    />
                  </div>
                  <label className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Checkbox
                      checked={place.includeInDocuments}
                      disabled={!isOwnerOrMember}
                      onCheckedChange={(checked) =>
                        handleUpdateAdditionalPlace(index, {
                          includeInDocuments: checked === true,
                        })
                      }
                    />
                    Include in generated documents
                  </label>
                </div>

                {isOwnerOrMember && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="justify-self-end"
                    onClick={() => handleRemoveAdditionalPlace(index)}
                    aria-label="Remove additional place of business"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            No additional places of business have been added yet.
          </p>
        )}
      </JobSettingsSection>

      <JobSettingsSection
        title="Bank details"
        description="Bank account opened for this job. Available in generated documents as the Job Bank Account fields."
        canEdit={isOwnerOrMember}
        isDirty={bankHasChanges}
        isSaving={savingSection === "bank"}
        onSave={() => {
          void handleSaveBank();
        }}
        onDiscard={() => {
          setBankName(job.bankName ?? "");
          setBankAccountName(job.bankAccountName ?? "");
          setBankBsb(job.bankBsb ?? "");
          setBankAccountNumber(job.bankAccountNumber ?? "");
        }}
      >
        <div className="grid grid-cols-2 gap-3">
          <Input
            placeholder="Bank name"
            value={bankName}
            onChange={(e) => setBankName(e.target.value)}
            disabled={!isOwnerOrMember}
          />
          <Input
            placeholder="Account name"
            value={bankAccountName}
            onChange={(e) => setBankAccountName(e.target.value)}
            disabled={!isOwnerOrMember}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input
            placeholder="BSB (000-000)"
            value={bankBsb}
            onChange={(e) => setBankBsb(e.target.value)}
            disabled={!isOwnerOrMember}
          />
          <Input
            placeholder="Account number"
            value={bankAccountNumber}
            onChange={(e) => setBankAccountNumber(e.target.value)}
            disabled={!isOwnerOrMember}
          />
        </div>
      </JobSettingsSection>

      <JobSettingsSection
        title="Status"
        canEdit={isOwnerOrMember}
        isDirty={statusHasChanges}
        isSaving={savingSection === "status"}
        onSave={() => {
          void handleSaveStatus();
        }}
        onDiscard={() => setStatus(job.status)}
      >
        <div className="flex flex-col gap-2">
          <Label>Status</Label>
          <Select
            value={status}
            onValueChange={setStatus}
            disabled={!isOwnerOrMember}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {JOB_STATUS_OPTIONS.filter(({ value }) => {
                if (value === "did_not_proceed") {
                  return job.status === "did_not_proceed";
                }
                if (job.status === "pre_appointment") {
                  return value === "pre_appointment";
                }
                return value !== "pre_appointment";
              }).map(({ value, label }) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {job.status === "pre_appointment" ? (
            <p className="text-xs text-muted-foreground">
              Pre-appointment jobs are converted via the{" "}
              <span className="font-medium text-foreground">
                Convert to Appointment
              </span>{" "}
              button on the Overview page (uploads the Notice of Appointment and
              flips the job to Day One). Use the{" "}
              <span className="font-medium text-foreground">
                Mark Did Not Proceed
              </span>{" "}
              action to close the pitch.
            </p>
          ) : null}
        </div>
      </JobSettingsSection>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Appointment Panel
// ---------------------------------------------------------------------------

/**
 * Appointment tab — owns every appointment-related setting in one place:
 * appointment type, appointment date, court / bankruptcy day-one
 * scalars, and the Notice of Appointment upload + extraction surface.
 *
 * Each group has its own Save so edits stay next to the fields they
 * belong to. The NOA panel underneath manages its own state (upload +
 * extract) so it doesn't participate in this form's save flow.
 */
function JobAppointmentPanel({
  job,
  onDirtyChange,
}: {
  job: JobData;
  onDirtyChange: (dirty: boolean) => void;
}) {
  const [appointmentType, setAppointmentType] = useState(
    job.appointmentType ?? "",
  );
  const [appointmentDate, setAppointmentDate] = useState(
    job.appointmentDate ?? "",
  );
  // FEG case-level dates — feed the service-at-appointment calculation
  // on the employee entitlements register. The administration start date
  // itself lives in `dayOneFields` (shared with the relation-back day).
  const [liquidationDate, setLiquidationDate] = useState(
    job.liquidationDate ?? "",
  );
  const [administrationContinuous, setAdministrationContinuous] = useState(
    job.administrationContinuous === undefined
      ? ""
      : job.administrationContinuous
        ? "yes"
        : "no",
  );
  // MOR-836 — appointee's state/territory, drives business-day offsets.
  const [appointeeJurisdiction, setAppointeeJurisdiction] = useState(
    job.appointeeJurisdiction ?? "",
  );
  // MOR-839 — per-job override for auto-generated statutory calendar tasks.
  const [statutoryTasksMode, setStatutoryTasksMode] = useState(
    job.statutoryTasksMode ?? "inherit",
  );
  const [notes, setNotes] = useState(job.notes ?? "");
  const [notesExpanded, setNotesExpanded] = useState(false);
  // Court/bankruptcy scalars — held as a single object so we can compare
  // and submit them as a block. Empty strings collapse to `undefined` on
  // save so a cleared field unsets rather than persists as "".
  const [dayOneFields, setDayOneFields] = useState<DayOneFieldsState>({
    courtName: job.courtName ?? "",
    courtState: job.courtState ?? "",
    courtNumber: job.courtNumber ?? "",
    sequestrationNumber: job.sequestrationNumber ?? "",
    proceedingMatterNumber: job.proceedingMatterNumber ?? "",
    proceedingYear: job.proceedingYear ?? "",
    courtOrderDate: job.courtOrderDate ?? "",
    windingUpApplicationDate: job.windingUpApplicationDate ?? "",
    administrationStartDate: job.administrationStartDate ?? "",
    plaintiffName: job.plaintiffName ?? "",
    defendantName: job.defendantName ?? "",
    bankruptName: job.bankruptName ?? "",
    bankruptDateOfBirth: job.bankruptDateOfBirth ?? "",
    administrationNumber: job.administrationNumber ?? "",
    petitioningCreditorCosts: job.petitioningCreditorCosts ?? "",
    actOfBankruptcyDate: job.actOfBankruptcyDate ?? "",
  });
  // MOR-901 — prior winding-up application. Held outside `dayOneFields`
  // because the status is a union and "unanswered" must stay distinct from
  // "no", while that state object is all strings.
  const [windingUpStatus, setWindingUpStatus] = useState<
    WindingUpApplicationStatus | undefined
  >(job.windingUpApplicationStatus);
  const [relationBackOverride, setRelationBackOverride] = useState(
    job.relationBackDateOverride ?? "",
  );
  // MOR-1252 — bankruptcy case record. The act date itself stays in
  // `dayOneFields` because NOA extraction and the create dialog write it
  // there; the section re-attaches it at render time.
  const [bankruptcyRecord, setBankruptcyRecord] = useState(
    bankruptcyRecordFromJob(job),
  );
  // MOR-661 QA-05: the case record reports whether its dates are internally
  // consistent; Save stays disabled while they are not.
  const [caseRecordValid, setCaseRecordValid] = useState(true);
  // MOR-901 — a change of appointment type is a conversion, not a field edit:
  // it needs the new appointment's own date, so picking a different type opens
  // the dialog rather than staging a local edit.
  const [pendingAppointmentType, setPendingAppointmentType] = useState<
    string | null
  >(null);
  const [savingSection, setSavingSection] = useState<
    "appointment" | "court" | "notes" | null
  >(null);

  const { mutateAsync: updateJob } = useMutation({
    mutationFn: useConvexMutation(api.jobs.mutations.updateJob),
  });

  const isOwnerOrMember = job.userRole === "owner" || job.userRole === "member";

  /**
   * Appointment-type picker handler. On a job that already has an appointment,
   * routes a genuine change into the change-of-appointment dialog so the new
   * appointment's date is captured and the outgoing one is preserved. Jobs with
   * no type yet (legacy rows) still set it inline via Save.
   */
  const handleAppointmentTypeChange = (next: string) => {
    if (job.appointmentType && next !== job.appointmentType) {
      setPendingAppointmentType(next);
      return;
    }
    setAppointmentType(next);
  };

  const hasDayOneChanges =
    dayOneFields.courtName !== (job.courtName ?? "") ||
    dayOneFields.courtState !== (job.courtState ?? "") ||
    dayOneFields.courtNumber !== (job.courtNumber ?? "") ||
    dayOneFields.sequestrationNumber !== (job.sequestrationNumber ?? "") ||
    dayOneFields.proceedingMatterNumber !==
      (job.proceedingMatterNumber ?? "") ||
    dayOneFields.proceedingYear !== (job.proceedingYear ?? "") ||
    dayOneFields.courtOrderDate !== (job.courtOrderDate ?? "") ||
    dayOneFields.windingUpApplicationDate !==
      (job.windingUpApplicationDate ?? "") ||
    dayOneFields.administrationStartDate !==
      (job.administrationStartDate ?? "") ||
    dayOneFields.plaintiffName !== (job.plaintiffName ?? "") ||
    dayOneFields.defendantName !== (job.defendantName ?? "") ||
    dayOneFields.bankruptName !== (job.bankruptName ?? "") ||
    dayOneFields.bankruptDateOfBirth !== (job.bankruptDateOfBirth ?? "") ||
    dayOneFields.administrationNumber !== (job.administrationNumber ?? "") ||
    dayOneFields.petitioningCreditorCosts !==
      (job.petitioningCreditorCosts ?? "") ||
    dayOneFields.actOfBankruptcyDate !== (job.actOfBankruptcyDate ?? "");

  // MOR-901 — tracked separately from the string-only day-one field block.
  const hasRelationBackChanges =
    windingUpStatus !== job.windingUpApplicationStatus ||
    relationBackOverride !== (job.relationBackDateOverride ?? "");

  // MOR-1252 — bankruptcy case record (the act date is tracked in
  // `dayOneFields`).
  const storedBankruptcyRecord = bankruptcyRecordFromJob(job);
  const hasBankruptcyChanges = (
    Object.keys(storedBankruptcyRecord) as Array<
      keyof typeof storedBankruptcyRecord
    >
  ).some((key) => bankruptcyRecord[key] !== storedBankruptcyRecord[key]);

  const isBankruptcyType = isBankruptcyAppointmentType(appointmentType);
  // The FEG "Insolvency Dates" block below resolves service across the
  // corporate administration → liquidation chain; a personal estate
  // (bankruptcy or Part X) has no such appointments, so it never applies.
  const isPersonal = isPersonalMatter(job);
  // Fields another section renders for this appointment type: the case
  // record owns the act of bankruptcy, and Job identity owns the bankrupt's
  // name and date of birth on a personal job (MOR-1858 QA-07).
  const dayOneOmitKeys: ReadonlySet<string> = new Set([
    ...(isBankruptcyType ? BANKRUPTCY_RECORD_DAY_ONE_KEYS : []),
    ...(isPersonalMatter(job) ? PERSONAL_IDENTITY_DAY_ONE_KEYS : []),
  ]);

  const storedContinuous =
    job.administrationContinuous === undefined
      ? ""
      : job.administrationContinuous
        ? "yes"
        : "no";

  const appointmentHasChanges =
    appointmentType !== (job.appointmentType ?? "") ||
    appointmentDate !== (job.appointmentDate ?? "") ||
    appointeeJurisdiction !== (job.appointeeJurisdiction ?? "") ||
    statutoryTasksMode !== (job.statutoryTasksMode ?? "inherit");
  const courtHasChanges =
    hasDayOneChanges ||
    hasRelationBackChanges ||
    hasBankruptcyChanges ||
    liquidationDate !== (job.liquidationDate ?? "") ||
    administrationContinuous !== storedContinuous;
  const notesHasChanges = notes !== (job.notes ?? "");
  const hasChanges =
    appointmentHasChanges || courtHasChanges || notesHasChanges;

  useEffect(() => {
    onDirtyChange(hasChanges);
    return () => onDirtyChange(false);
  }, [hasChanges, onDirtyChange]);

  useEffect(() => {
    if (!appointmentHasChanges) {
      setAppointmentType(job.appointmentType ?? "");
      setAppointmentDate(job.appointmentDate ?? "");
      setAppointeeJurisdiction(job.appointeeJurisdiction ?? "");
      setStatutoryTasksMode(job.statutoryTasksMode ?? "inherit");
    }
    if (!courtHasChanges) {
      setLiquidationDate(job.liquidationDate ?? "");
      setAdministrationContinuous(
        job.administrationContinuous === undefined
          ? ""
          : job.administrationContinuous
            ? "yes"
            : "no",
      );
      setDayOneFields({
        courtName: job.courtName ?? "",
        courtState: job.courtState ?? "",
        courtNumber: job.courtNumber ?? "",
        sequestrationNumber: job.sequestrationNumber ?? "",
        proceedingMatterNumber: job.proceedingMatterNumber ?? "",
        proceedingYear: job.proceedingYear ?? "",
        courtOrderDate: job.courtOrderDate ?? "",
        windingUpApplicationDate: job.windingUpApplicationDate ?? "",
        administrationStartDate: job.administrationStartDate ?? "",
        plaintiffName: job.plaintiffName ?? "",
        defendantName: job.defendantName ?? "",
        bankruptName: job.bankruptName ?? "",
        bankruptDateOfBirth: job.bankruptDateOfBirth ?? "",
        administrationNumber: job.administrationNumber ?? "",
        petitioningCreditorCosts: job.petitioningCreditorCosts ?? "",
        actOfBankruptcyDate: job.actOfBankruptcyDate ?? "",
      });
      setWindingUpStatus(job.windingUpApplicationStatus);
      setRelationBackOverride(job.relationBackDateOverride ?? "");
      setBankruptcyRecord(bankruptcyRecordFromJob(job));
    }
    if (!notesHasChanges) {
      setNotes(job.notes ?? "");
    }
  }, [job, appointmentHasChanges, courtHasChanges, notesHasChanges]);

  const saveAppointmentSection = async (
    section: "appointment" | "court" | "notes",
    payload: UpdateJobFields,
    successMessage: string,
  ) => {
    setSavingSection(section);
    try {
      await updateJob({ jobId: job._id, ...payload });
      toast.success(successMessage);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to update appointment";
      toast.error(message);
    } finally {
      setSavingSection(null);
    }
  };

  const handleSaveAppointment = () =>
    saveAppointmentSection(
      "appointment",
      {
        appointmentType: appointmentType
          ? (appointmentType as AppointmentType)
          : undefined,
        appointmentDate: appointmentDate || undefined,
        appointeeJurisdiction,
        statutoryTasksMode,
      },
      "Appointment details updated",
    );

  const handleDiscardAppointment = () => {
    setAppointmentType(job.appointmentType ?? "");
    setAppointmentDate(job.appointmentDate ?? "");
    setAppointeeJurisdiction(job.appointeeJurisdiction ?? "");
    setStatutoryTasksMode(job.statutoryTasksMode ?? "inherit");
  };

  const handleSaveCourt = () =>
    saveAppointmentSection(
      "court",
      {
        liquidationDate: liquidationDate || undefined,
        administrationContinuous:
          administrationContinuous === ""
            ? undefined
            : administrationContinuous === "yes",
        courtName: dayOneFields.courtName.trim() || undefined,
        courtState: dayOneFields.courtState.trim() || undefined,
        courtNumber: dayOneFields.courtNumber.trim() || undefined,
        sequestrationNumber:
          dayOneFields.sequestrationNumber.trim() || undefined,
        proceedingMatterNumber:
          dayOneFields.proceedingMatterNumber.trim() || undefined,
        proceedingYear: dayOneFields.proceedingYear.trim() || undefined,
        courtOrderDate: dayOneFields.courtOrderDate.trim() || undefined,
        windingUpApplicationDate:
          dayOneFields.windingUpApplicationDate.trim() || undefined,
        administrationStartDate:
          dayOneFields.administrationStartDate.trim() || undefined,
        plaintiffName: dayOneFields.plaintiffName.trim() || undefined,
        defendantName: dayOneFields.defendantName.trim() || undefined,
        bankruptName: dayOneFields.bankruptName.trim() || undefined,
        // MOR-1847 — "" clears a recorded date of birth; otherwise an empty
        // field is left out so saving doesn't log a no-op change.
        bankruptDateOfBirth:
          dayOneFields.bankruptDateOfBirth.trim() ||
          (job.bankruptDateOfBirth ? "" : undefined),
        administrationNumber:
          dayOneFields.administrationNumber.trim() || undefined,
        petitioningCreditorCosts:
          dayOneFields.petitioningCreditorCosts.trim() || undefined,
        actOfBankruptcyDate:
          dayOneFields.actOfBankruptcyDate.trim() || undefined,
        windingUpApplicationStatus: windingUpStatus,
        relationBackDateOverride: relationBackOverride,
        // MOR-1252 — bankruptcy case record. Dates and the description are
        // sent verbatim so clearing them unsets the field.
        bankruptcyPetitionType: bankruptcyRecord.petitionType || undefined,
        petitionPresentedDate: bankruptcyRecord.petitionPresentedDate,
        dateOfBankruptcy: bankruptcyRecord.dateOfBankruptcy,
        actOfBankruptcyParagraph:
          bankruptcyRecord.actOfBankruptcyParagraph || undefined,
        actOfBankruptcyDescription: bankruptcyRecord.actOfBankruptcyDescription,
        creditorsPetitionPending:
          bankruptcyRecord.creditorsPetitionPending === ""
            ? undefined
            : bankruptcyRecord.creditorsPetitionPending === "yes",
        courtSpecifiedCommencementDate:
          bankruptcyRecord.courtSpecifiedCommencementDate,
        // MOR-1255 — insolvency evidence and the s 115(1A) flag.
        bankruptCarriedOnBusiness:
          bankruptcyRecord.carriedOnBusiness === ""
            ? undefined
            : bankruptcyRecord.carriedOnBusiness === "yes",
        earliestUnpaidJudgmentDate: bankruptcyRecord.earliestUnpaidJudgmentDate,
        statementOfAffairsInsolvencyDate:
          bankruptcyRecord.statementOfAffairsInsolvencyDate,
        bankruptcyNoticeExtensionDisregarded:
          bankruptcyRecord.extensionDisregarded,
      },
      "Court and insolvency dates updated",
    );

  const handleDiscardCourt = () => {
    setLiquidationDate(job.liquidationDate ?? "");
    setAdministrationContinuous(
      job.administrationContinuous === undefined
        ? ""
        : job.administrationContinuous
          ? "yes"
          : "no",
    );
    setDayOneFields({
      courtName: job.courtName ?? "",
      courtState: job.courtState ?? "",
      courtNumber: job.courtNumber ?? "",
      sequestrationNumber: job.sequestrationNumber ?? "",
      proceedingMatterNumber: job.proceedingMatterNumber ?? "",
      proceedingYear: job.proceedingYear ?? "",
      courtOrderDate: job.courtOrderDate ?? "",
      windingUpApplicationDate: job.windingUpApplicationDate ?? "",
      administrationStartDate: job.administrationStartDate ?? "",
      plaintiffName: job.plaintiffName ?? "",
      defendantName: job.defendantName ?? "",
      bankruptName: job.bankruptName ?? "",
      bankruptDateOfBirth: job.bankruptDateOfBirth ?? "",
      administrationNumber: job.administrationNumber ?? "",
      petitioningCreditorCosts: job.petitioningCreditorCosts ?? "",
      actOfBankruptcyDate: job.actOfBankruptcyDate ?? "",
    });
    setWindingUpStatus(job.windingUpApplicationStatus);
    setRelationBackOverride(job.relationBackDateOverride ?? "");
    setBankruptcyRecord(bankruptcyRecordFromJob(job));
  };

  const handleSaveNotes = () =>
    saveAppointmentSection(
      "notes",
      { notes: notes.trim() || undefined },
      "Appointment notes updated",
    );

  return (
    <div className="flex flex-col gap-6">
      <p className="text-sm text-muted-foreground">
        Appointment data is automatically extracted from any Instrument of
        Appointment and is added to the Job Fields for document generation.
      </p>

      <JobSettingsSection
        title="Appointment"
        canEdit={isOwnerOrMember}
        isDirty={appointmentHasChanges}
        isSaving={savingSection === "appointment"}
        onSave={() => {
          void handleSaveAppointment();
        }}
        onDiscard={handleDiscardAppointment}
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-2">
            <Label>Appointment Type</Label>
            <Select
              value={appointmentType}
              onValueChange={handleAppointmentTypeChange}
              disabled={!isOwnerOrMember}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select appointment type" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(APPOINTMENT_TYPE_LABELS).map(
                  ([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ),
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-2">
            <Label>Appointment Date</Label>
            <DatePicker
              value={appointmentDate}
              onChange={setAppointmentDate}
              placeholder="Select appointment date"
              disabled={!isOwnerOrMember}
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label>Appointee Jurisdiction</Label>
            <Select
              value={appointeeJurisdiction || "__none__"}
              onValueChange={(v) =>
                setAppointeeJurisdiction(v === "__none__" ? "" : v)
              }
              disabled={!isOwnerOrMember}
            >
              <SelectTrigger>
                <SelectValue placeholder="Firm default" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Firm default</SelectItem>
                {AU_JURISDICTIONS.map((j) => (
                  <SelectItem key={j} value={j}>
                    {j}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] leading-snug text-muted-foreground">
              The appointee&apos;s state or territory — sets the public-holiday
              calendar used for business-day due dates in workflows.
            </p>
          </div>

          <div className="flex flex-col gap-2">
            <Label>Statutory Calendar Tasks</Label>
            <Select
              value={statutoryTasksMode}
              onValueChange={(v) =>
                setStatutoryTasksMode(v as "inherit" | "on" | "off")
              }
              disabled={!isOwnerOrMember}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="inherit">Firm default</SelectItem>
                <SelectItem value="on">Shown on this job</SelectItem>
                <SelectItem value="off">Hidden on this job</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[11px] leading-snug text-muted-foreground">
              Controls the pre-populated statutory lodgement tasks on this
              job&apos;s calendar. Hide them if your own workflow already covers
              them.
            </p>
          </div>
        </div>
      </JobSettingsSection>

      <JobSettingsSection
        title="Court and insolvency dates"
        description="Court, bankruptcy, relation-back, and Fair Entitlements Guarantee dates. Extracted from the Instrument of Appointment where available."
        canEdit={isOwnerOrMember}
        isDirty={courtHasChanges}
        isSaving={savingSection === "court"}
        saveDisabled={!caseRecordValid}
        onSave={() => {
          void handleSaveCourt();
        }}
        onDiscard={handleDiscardCourt}
      >
        <DayOneFieldsSection
          appointmentType={appointmentType}
          values={dayOneFields}
          onChange={setDayOneFields}
          disabled={!isOwnerOrMember}
          omitKeys={dayOneOmitKeys}
        />

        {isBankruptcyType && (
          <BankruptcyCaseRecordSection
            values={{
              ...bankruptcyRecord,
              actOfBankruptcyDate: dayOneFields.actOfBankruptcyDate,
            }}
            onChange={({ actOfBankruptcyDate, ...rest }) => {
              setBankruptcyRecord(rest);
              setDayOneFields({ ...dayOneFields, actOfBankruptcyDate });
            }}
            context={{
              appointmentDate,
              courtOrderDate: dayOneFields.courtOrderDate,
            }}
            override={relationBackOverride}
            onOverrideChange={setRelationBackOverride}
            onValidityChange={setCaseRecordValid}
            appointmentType={appointmentType}
            disabled={!isOwnerOrMember}
          />
        )}

        <RelationBackFieldsSection
          appointmentType={appointmentType}
          context={{
            appointmentDate,
            administrationStartDate: dayOneFields.administrationStartDate,
          }}
          applicationDate={dayOneFields.windingUpApplicationDate}
          onApplicationDateChange={(next) =>
            setDayOneFields({
              ...dayOneFields,
              windingUpApplicationDate: next,
            })
          }
          status={windingUpStatus}
          onStatusChange={setWindingUpStatus}
          override={relationBackOverride}
          onOverrideChange={setRelationBackOverride}
          disabled={!isOwnerOrMember}
        />

        {/* Insolvency dates — resolve which appointment date the FEG
            service calculation measures employee service to. Corporate only:
            a personal estate has no administration/liquidation chain. */}
        {!isPersonal && (
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <h3 className="text-sm font-medium">Insolvency Dates</h3>
              <p className="text-xs text-muted-foreground">
                Used to calculate each employee&apos;s years of service for the
                Fair Entitlements Guarantee. Where the administration ran
                continuously into the liquidation, service is measured to the
                administration start date; otherwise to the liquidation date.
                For liquidation appointments the administration start date is
                entered with the appointment details above and also sets the
                relation-back day.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {!["cvl", "court_liquidation", "simplified_liquidation"].includes(
                appointmentType,
              ) && (
                <div className="flex flex-col gap-1.5">
                  <Label>Administration Start Date</Label>
                  <DatePicker
                    value={dayOneFields.administrationStartDate}
                    onChange={(next) =>
                      setDayOneFields({
                        ...dayOneFields,
                        administrationStartDate: next ?? "",
                      })
                    }
                    placeholder="Select date"
                    disabled={!isOwnerOrMember}
                  />
                </div>
              )}
              <div className="flex flex-col gap-1.5">
                <Label>Liquidation Date</Label>
                <DatePicker
                  value={liquidationDate}
                  onChange={setLiquidationDate}
                  placeholder="Select date"
                  disabled={!isOwnerOrMember}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Administration Continuous?</Label>
                <Select
                  value={administrationContinuous}
                  onValueChange={setAdministrationContinuous}
                  disabled={!isOwnerOrMember}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Not set" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}
      </JobSettingsSection>

      <JobSettingsSection
        title="Notes from Instrument of Appointment"
        description="Automatically extracted from the Instrument of Appointment — all notes are editable."
        canEdit={isOwnerOrMember}
        isDirty={notesHasChanges}
        isSaving={savingSection === "notes"}
        onSave={() => {
          void handleSaveNotes();
        }}
        onDiscard={() => setNotes(job.notes ?? "")}
      >
        <div className="relative">
          <div
            className={cn(
              "overflow-hidden transition-[max-height] duration-200",
              !notesExpanded && "max-h-[120px]",
            )}
          >
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Notes extracted from the Instrument of Appointment"
              className={cn(
                "min-h-[100px] resize-none",
                notesExpanded && "min-h-[200px]",
              )}
              disabled={!isOwnerOrMember}
            />
          </div>
          {!notesExpanded && notes.length > 200 && (
            <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-background to-transparent" />
          )}
        </div>
        {notes.length > 200 && (
          <button
            type="button"
            onClick={() => setNotesExpanded(!notesExpanded)}
            className="mt-1 self-start rounded-md border border-primary/30 px-3 py-1 text-xs font-medium text-primary transition-colors hover:border-primary/50 hover:bg-primary/5"
          >
            {notesExpanded ? "Show less" : "Show more"}
          </button>
        )}
      </JobSettingsSection>

      {/* MOR-901 — appointment history lives here rather than only on the
          History tab: the chain and the dates behind it are what a
          practitioner is checking while reading appointment details. */}
      <AppointmentHistorySection
        jobId={job._id}
        priorAppointments={job.priorAppointments}
        currentAppointmentType={job.appointmentType}
        currentAppointmentDate={job.appointmentDate}
      />

      {/* Notice of Appointment — upload, extraction, and review surface */}
      <section className="flex flex-col gap-3">
        <JobNoticeOfAppointmentPanel
          jobId={job._id}
          userRole={job.userRole as JobRole}
        />
      </section>

      {job.appointmentType && (
        <RecordAppointmentChangeDialog
          jobId={job._id}
          currentAppointmentType={job.appointmentType}
          currentAppointmentDate={job.appointmentDate}
          initialAppointmentType={pendingAppointmentType ?? undefined}
          open={pendingAppointmentType !== null}
          onOpenChange={(next) => {
            if (!next) setPendingAppointmentType(null);
          }}
        />
      )}
    </div>
  );
}

/**
 * Change-log actions that are inherently about the appointment.
 */
const APPOINTMENT_HISTORY_ACTIONS = new Set([
  "converted_to_appointment",
  "appointment_changed",
  "notice_of_appointment_uploaded",
]);

/**
 * Job fields owned by the Appointment tab. A `field_changed` entry is shown in
 * the appointment history when it touches one of these, and the diff is
 * narrowed to them so an unrelated edit saved in the same action (a renamed
 * job, say) doesn't leak into the appointment's audit trail.
 */
const APPOINTMENT_HISTORY_FIELDS = new Set([
  "appointmentType",
  "appointmentDate",
  "administrationStartDate",
  "liquidationDate",
  "administrationContinuous",
  "windingUpApplicationDate",
  "windingUpApplicationStatus",
  "relationBackDateOverride",
  "courtName",
  "courtState",
  "courtNumber",
  "sequestrationNumber",
  "proceedingMatterNumber",
  "proceedingYear",
  "courtOrderDate",
  "plaintiffName",
  "defendantName",
  "bankruptName",
  "bankruptDateOfBirth",
  "administrationNumber",
  "petitioningCreditorCosts",
  "actOfBankruptcyDate",
  // MOR-1252 — bankruptcy case record
  "bankruptcyPetitionType",
  "petitionPresentedDate",
  "dateOfBankruptcy",
  "actOfBankruptcyParagraph",
  "actOfBankruptcyDescription",
  "creditorsPetitionPending",
  "courtSpecifiedCommencementDate",
  "bankruptCarriedOnBusiness",
  "earliestUnpaidJudgmentDate",
  "statementOfAffairsInsolvencyDate",
  "bankruptcyNoticeExtensionDisregarded",
]);

/** Narrows a before/after record to the appointment-owned keys. */
function pickAppointmentFields(
  values: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  if (!values) return undefined;
  const picked = Object.fromEntries(
    Object.entries(values).filter(([key]) =>
      APPOINTMENT_HISTORY_FIELDS.has(key),
    ),
  );
  return Object.keys(picked).length > 0 ? picked : undefined;
}

/**
 * Appointment history, shown inside the Appointment Details section.
 *
 * Purpose: a practitioner reading appointment details is usually checking how
 * the job got to its current appointment — that the administration's dates
 * survived a VA → DOCA → liquidation sequence, and who changed what. Those
 * dates still drive the relation-back day and the Fair Entitlements Guarantee
 * service calculation long after the administration itself ended, so keeping
 * this a tab away made it easy to miss.
 *
 * Two parts: the appointment chain (what the job has been), then the audit
 * trail of appointment changes (who changed it, when, and from what).
 * Renders nothing at all when the job has neither.
 */
function AppointmentHistorySection({
  jobId,
  priorAppointments,
  currentAppointmentType,
  currentAppointmentDate,
}: {
  jobId: Id<"jobs">;
  priorAppointments?: JobData["priorAppointments"];
  currentAppointmentType?: string;
  currentAppointmentDate?: string;
}) {
  const { data: changeLog } = useQuery(
    convexQuery(api.jobs.queries.listJobChangeLog, { jobId, limit: 200 }),
  );

  // Oldest first, ending with the appointment currently in force — reads as a
  // sequence, unlike the reverse-chronological audit trail below it.
  const chain = [
    ...(priorAppointments ?? []).map((prior) => ({
      appointmentType: prior.appointmentType,
      appointmentDate: prior.appointmentDate,
      note: prior.note,
      isCurrent: false,
    })),
    ...(currentAppointmentType
      ? [
          {
            appointmentType: currentAppointmentType,
            appointmentDate: currentAppointmentDate,
            note: undefined as string | undefined,
            isCurrent: true,
          },
        ]
      : []),
  ];

  const appointmentEntries = (changeLog ?? []).flatMap((entry) => {
    if (APPOINTMENT_HISTORY_ACTIONS.has(entry.action)) return [entry];
    if (entry.action !== "field_changed") return [];
    const after = pickAppointmentFields(entry.after);
    if (!after) return [];
    return [{ ...entry, before: pickAppointmentFields(entry.before), after }];
  });

  const hasChain = chain.length > 1;
  if (!hasChain && appointmentEntries.length === 0) return null;

  return (
    <div className="flex flex-col gap-3 border-t pt-6">
      <div className="flex flex-col gap-1">
        <h3 className="text-sm font-medium">Appointment History</h3>
        <p className="text-xs text-muted-foreground">
          {hasChain
            ? "Every appointment this job has held, and the changes made to the appointment details. Earlier appointments keep their dates because they remain relevant — the administration start date still anchors the relation-back day and the Fair Entitlements Guarantee service calculation."
            : "Changes made to this job's appointment details."}
        </p>
      </div>

      {hasChain && (
        <ol className="flex flex-col gap-2">
          {chain.map((entry, index) => (
            <li
              key={`${entry.appointmentType}-${index}`}
              className="flex flex-col gap-1 rounded-md border px-3 py-2.5"
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium">
                  {APPOINTMENT_TYPE_LABELS[entry.appointmentType] ??
                    entry.appointmentType}
                </span>
                {entry.appointmentDate && (
                  <span className="text-sm text-muted-foreground">
                    {entry.appointmentDate}
                  </span>
                )}
                {entry.isCurrent && (
                  <Badge color="green" size="sm">
                    Current
                  </Badge>
                )}
              </div>
              {entry.note && (
                <p className="text-xs text-muted-foreground">{entry.note}</p>
              )}
            </li>
          ))}
        </ol>
      )}

      {appointmentEntries.length > 0 && (
        <ol className="flex flex-col gap-2">
          {appointmentEntries.map((entry) => (
            <ChangeLogEntry key={entry._id} entry={entry} />
          ))}
        </ol>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Document Tags Panel
// ---------------------------------------------------------------------------

/**
 * Document Tags tab — lets owners/members pick the document-tag options
 * (driven by the org's `tagGroups`) that determine which precedent templates
 * auto-populate when generating documents for this job.
 */
/**
 * Document Setup panel — consolidates the per-job document-generation
 * configuration in one place: the multi-select tag groups
 * (`jobs.customTagSelections`) that decide which templates auto-populate,
 * and the single-select variant groups (`jobs.variantSelections`) that
 * pick the job-wide variant default applied across every generated
 * document. Both are needed to fully configure document generation, so
 * they share one panel and one Save action.
 */
// MOR-429: maps a canonical snake_case key (as declared in
// FIELD_CATEGORIES → "court" category) to the camelCase state key in
// `dayOneFields`. The two halves of the project use different cases by
// convention — canonical fields are snake_case so templates can use them
// as {{placeholder}}, but the Convex `jobs` table uses camelCase like
// the rest of the schema. This map bridges the two.
const DAY_ONE_KEY_MAP: Record<
  string,
  | "courtName"
  | "courtState"
  | "courtNumber"
  | "sequestrationNumber"
  | "proceedingMatterNumber"
  | "proceedingYear"
  | "courtOrderDate"
  | "windingUpApplicationDate"
  | "administrationStartDate"
  | "plaintiffName"
  | "defendantName"
  | "bankruptName"
  | "bankruptDateOfBirth"
  | "administrationNumber"
  | "petitioningCreditorCosts"
  | "actOfBankruptcyDate"
> = {
  court_name: "courtName",
  court_state: "courtState",
  court_number: "courtNumber",
  sequestration_number: "sequestrationNumber",
  proceeding_matter_number: "proceedingMatterNumber",
  proceeding_year: "proceedingYear",
  court_order_date: "courtOrderDate",
  winding_up_application_date: "windingUpApplicationDate",
  administration_start_date: "administrationStartDate",
  plaintiff_name: "plaintiffName",
  defendant_name: "defendantName",
  bankrupt_name: "bankruptName",
  bankrupt_date_of_birth: "bankruptDateOfBirth",
  administration_number: "administrationNumber",
  petitioning_creditor_costs: "petitioningCreditorCosts",
  act_of_bankruptcy_date: "actOfBankruptcyDate",
};

/** Canonical keys the bankruptcy case record section renders itself (MOR-1252). */
const BANKRUPTCY_RECORD_DAY_ONE_KEYS: ReadonlySet<string> = new Set([
  "act_of_bankruptcy_date",
]);

/** Canonical keys the Job identity section edits on a personal job (MOR-1858 QA-07). */
const PERSONAL_IDENTITY_DAY_ONE_KEYS: ReadonlySet<string> = new Set([
  "bankrupt_name",
  "bankrupt_date_of_birth",
]);

/** Form state for the bankruptcy case record, derived from the job (MOR-1252). */
function bankruptcyRecordFromJob(
  job: JobData,
): Omit<BankruptcyCaseRecordState, "actOfBankruptcyDate"> {
  return {
    petitionType: job.bankruptcyPetitionType ?? "",
    petitionPresentedDate: job.petitionPresentedDate ?? "",
    dateOfBankruptcy: job.dateOfBankruptcy ?? "",
    actOfBankruptcyParagraph: job.actOfBankruptcyParagraph ?? "",
    actOfBankruptcyDescription: job.actOfBankruptcyDescription ?? "",
    creditorsPetitionPending:
      job.creditorsPetitionPending === undefined
        ? ""
        : job.creditorsPetitionPending
          ? "yes"
          : "no",
    courtSpecifiedCommencementDate: job.courtSpecifiedCommencementDate ?? "",
    carriedOnBusiness:
      job.bankruptCarriedOnBusiness === undefined
        ? ""
        : job.bankruptCarriedOnBusiness
          ? "yes"
          : "no",
    earliestUnpaidJudgmentDate: job.earliestUnpaidJudgmentDate ?? "",
    statementOfAffairsInsolvencyDate:
      job.statementOfAffairsInsolvencyDate ?? "",
    extensionDisregarded: job.bankruptcyNoticeExtensionDisregarded ?? false,
  };
}

interface DayOneFieldsState {
  courtName: string;
  courtState: string;
  courtNumber: string;
  sequestrationNumber: string;
  proceedingMatterNumber: string;
  proceedingYear: string;
  courtOrderDate: string;
  windingUpApplicationDate: string;
  administrationStartDate: string;
  plaintiffName: string;
  defendantName: string;
  bankruptName: string;
  bankruptDateOfBirth: string;
  administrationNumber: string;
  petitioningCreditorCosts: string;
  actOfBankruptcyDate: string;
}

/**
 * Editable section for court / bankruptcy day-one fields. Filters fields
 * by the canonical `appointmentTypes` applicability hint so a CVL job
 * doesn't see bankruptcy fields and vice versa. Hidden entirely when no
 * fields apply to the current appointment type. Values are populated
 * automatically by NOA extraction (see `convex/jobs/noticeOfAppointment`)
 * and shown here for review/correction.
 */
function DayOneFieldsSection({
  appointmentType,
  values,
  onChange,
  disabled,
  omitKeys,
}: {
  appointmentType: string;
  values: DayOneFieldsState;
  onChange: (next: DayOneFieldsState) => void;
  disabled: boolean;
  /** Canonical keys rendered by another section on this appointment type. */
  omitKeys?: ReadonlySet<string>;
}) {
  const courtCategory = FIELD_CATEGORIES.find((c) => c.id === "court");
  if (!courtCategory) return null;

  // Filter the court-category fields to those that apply to the current
  // appointment type. Unscoped fields always show; if no appointmentType
  // is set yet we err toward showing everything (advisory only).
  const visibleFields = courtCategory.fields
    .filter((f) => DAY_ONE_KEY_MAP[f.key])
    .filter((f) => !omitKeys?.has(f.key))
    .filter((f) =>
      appointmentTypeAppliesToField(
        f,
        appointmentType
          ? (appointmentType as Parameters<
              typeof appointmentTypeAppliesToField
            >[1])
          : undefined,
      ),
    );

  if (visibleFields.length === 0) return null;

  const sectionTitle =
    appointmentType && APPOINTMENT_TYPE_LABELS[appointmentType]
      ? `${APPOINTMENT_TYPE_LABELS[appointmentType]} Details`
      : "Court / Bankruptcy Details";

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border/50 bg-secondary/20 p-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-semibold">{sectionTitle}</h4>
          <p className="text-xs text-muted-foreground">
            Automatically extracted from any Instrument of Appointment. All
            fields are editable.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {visibleFields.map((field) => {
          const stateKey = DAY_ONE_KEY_MAP[field.key];
          const value = values[stateKey];
          const isDate = field.fieldType === "date";
          return (
            <div key={field.key} className="flex flex-col gap-1.5">
              <Label htmlFor={`day-one-${field.key}`}>{field.label}</Label>
              {isDate ? (
                <DatePicker
                  value={value}
                  onChange={(next) =>
                    onChange({ ...values, [stateKey]: next ?? "" })
                  }
                  placeholder={field.placeholder ?? "Select date"}
                  disabled={disabled}
                />
              ) : (
                <Input
                  id={`day-one-${field.key}`}
                  value={value}
                  onChange={(e) =>
                    onChange({ ...values, [stateKey]: e.target.value })
                  }
                  placeholder={field.placeholder}
                  disabled={disabled}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Structural equality for a tag/variant selection record. Values are either
 * a single string (variant selections) or a string array (custom tag
 * selections). The toggle handlers below mutate these records via
 * `delete` + spread, so the local record's key order can drift from the
 * server's even when every entry is identical — comparing with
 * `JSON.stringify` would then report a spurious difference and keep the
 * section "Unsaved" forever. Compare by entry instead.
 */
function sameSelectionRecord(
  a: Record<string, string | string[]>,
  b: Record<string, string | string[]>,
): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  return [...keys].every((key) => {
    const left = a[key];
    const right = b[key];
    if (Array.isArray(left) || Array.isArray(right)) {
      const leftArray = Array.isArray(left) ? left : [];
      const rightArray = Array.isArray(right) ? right : [];
      return (
        leftArray.length === rightArray.length &&
        leftArray.every((value, index) => value === rightArray[index])
      );
    }
    return left === right;
  });
}

function JobDocumentSetupPanel({
  job,
  onDirtyChange,
}: {
  job: JobData;
  onDirtyChange: (dirty: boolean) => void;
}) {
  const [customTagSelections, setCustomTagSelections] = useState<
    Record<string, string[]>
  >(job.customTagSelections ?? {});
  const [variantSelections, setVariantSelections] = useState<
    Record<string, string>
  >(job.variantSelections ?? {});

  const [savingSection, setSavingSection] = useState<
    "variants" | "tags" | null
  >(null);

  const { data: tagGroups, isLoading: tagsLoading } = useQuery({
    ...convexQuery(api.settings.precedents.tagGroups.listTagGroups, {
      orgId: job.orgId,
    }),
  });

  const { data: variantGroups, isLoading: variantsLoading } = useQuery({
    ...convexQuery(api.settings.precedents.variantGroups.listVariantGroups, {
      orgId: job.orgId,
    }),
  });

  const { mutateAsync: updateJob } = useMutation({
    mutationFn: useConvexMutation(api.jobs.mutations.updateJob),
  });

  const isOwnerOrMember = job.userRole === "owner" || job.userRole === "member";

  const variantsHasChanges = !sameSelectionRecord(
    variantSelections,
    job.variantSelections ?? {},
  );
  const tagsHasChanges = !sameSelectionRecord(
    customTagSelections,
    job.customTagSelections ?? {},
  );
  const hasChanges = variantsHasChanges || tagsHasChanges;

  useEffect(() => {
    onDirtyChange(hasChanges);
    return () => onDirtyChange(false);
  }, [hasChanges, onDirtyChange]);

  useEffect(() => {
    if (!variantsHasChanges) {
      setVariantSelections(job.variantSelections ?? {});
    }
    if (!tagsHasChanges) {
      setCustomTagSelections(job.customTagSelections ?? {});
    }
  }, [job, variantsHasChanges, tagsHasChanges]);

  const saveDocumentSection = async (
    section: "variants" | "tags",
    payload: UpdateJobFields,
    successMessage: string,
  ) => {
    setSavingSection(section);
    try {
      await updateJob({ jobId: job._id, ...payload });
      toast.success(successMessage);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to update document settings";
      toast.error(message);
    } finally {
      setSavingSection(null);
    }
  };

  const handleSaveVariants = () => {
    const hasVariants = Object.keys(variantSelections).length > 0;
    return saveDocumentSection(
      "variants",
      { variantSelections: hasVariants ? variantSelections : undefined },
      "Document variants updated",
    );
  };

  const handleSaveTags = () => {
    const hasCustomTags = Object.values(customTagSelections).some(
      (v) => v.length > 0,
    );
    return saveDocumentSection(
      "tags",
      { customTagSelections: hasCustomTags ? customTagSelections : undefined },
      "Document tags updated",
    );
  };

  if (tagsLoading || variantsLoading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const tagGroupsList: Doc<"templateTagGroups">[] = tagGroups ?? [];
  const variantGroupsList: Doc<"variantGroups">[] = variantGroups ?? [];

  return (
    <div className="flex flex-col gap-6">
      <JobSettingsSection
        title="Document variants"
        description="The job-wide variant applied to every generated document. Individual documents can still override this when needed."
        canEdit={isOwnerOrMember}
        isDirty={variantsHasChanges}
        isSaving={savingSection === "variants"}
        onSave={() => {
          void handleSaveVariants();
        }}
        onDiscard={() => setVariantSelections(job.variantSelections ?? {})}
      >
        {variantGroupsList.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No document variant groups have been configured for this
            organisation yet. Add them under organisation settings.
          </p>
        ) : (
          <div className="space-y-3">
            {variantGroupsList.map((group) => {
              const groupSelection = variantSelections[group.slug];
              return (
                <div key={group._id} className="flex flex-col gap-1.5">
                  <span className="text-xs font-medium text-foreground/70">
                    {group.name}
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {/* Explicit "None" chip — highlighted when no job-wide
                        default is chosen for this group, so an unset default is
                        a visible, deliberate state rather than a blank row. */}
                    {(() => {
                      const noneSelected = groupSelection === undefined;
                      return (
                        <button
                          key="__none__"
                          type="button"
                          disabled={!isOwnerOrMember}
                          className={cn(
                            "flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs transition",
                            noneSelected
                              ? "border-primary/40 bg-primary/10 text-primary"
                              : "border-border text-muted-foreground hover:border-border/80 hover:text-foreground",
                            !isOwnerOrMember && "cursor-not-allowed opacity-60",
                          )}
                          onClick={() => {
                            setVariantSelections((prev) => {
                              const next = { ...prev };
                              delete next[group.slug];
                              return next;
                            });
                          }}
                        >
                          <div
                            className={cn(
                              "flex size-3.5 shrink-0 items-center justify-center rounded-full border transition",
                              noneSelected ? "border-primary" : "border-border",
                            )}
                          >
                            {noneSelected && (
                              <div className="size-1.5 rounded-full bg-primary" />
                            )}
                          </div>
                          None
                        </button>
                      );
                    })()}
                    {group.options.map(
                      (opt: { value: string; label: string }) => {
                        const selected = groupSelection === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            disabled={!isOwnerOrMember}
                            className={cn(
                              "flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs transition",
                              selected
                                ? "border-primary/40 bg-primary/10 text-primary"
                                : "border-border text-muted-foreground hover:border-border/80 hover:text-foreground",
                              !isOwnerOrMember &&
                                "cursor-not-allowed opacity-60",
                            )}
                            onClick={() => {
                              setVariantSelections((prev) => {
                                const next = { ...prev };
                                if (selected) delete next[group.slug];
                                else next[group.slug] = opt.value;
                                return next;
                              });
                            }}
                          >
                            <div
                              className={cn(
                                "flex size-3.5 shrink-0 items-center justify-center rounded-full border transition",
                                selected ? "border-primary" : "border-border",
                              )}
                            >
                              {selected && (
                                <div className="size-1.5 rounded-full bg-primary" />
                              )}
                            </div>
                            {opt.label}
                          </button>
                        );
                      },
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </JobSettingsSection>

      <JobSettingsSection
        title="Document tags"
        description="Selections here determine which templates auto-populate when generating documents for this job."
        canEdit={isOwnerOrMember}
        isDirty={tagsHasChanges}
        isSaving={savingSection === "tags"}
        onSave={() => {
          void handleSaveTags();
        }}
        onDiscard={() => setCustomTagSelections(job.customTagSelections ?? {})}
      >
        {tagGroupsList.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No document tag groups have been configured for this organisation
            yet. Add them under organisation settings.
          </p>
        ) : (
          <div className="space-y-3">
            {tagGroupsList.map((group) => (
              <div key={group._id} className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-foreground/70">
                  {group.name}
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {group.options.map(
                    (opt: { value: string; label: string }) => {
                      const selected =
                        customTagSelections[group.slug]?.includes(opt.value) ??
                        false;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          disabled={!isOwnerOrMember}
                          className={cn(
                            "flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs transition",
                            selected
                              ? "border-primary/40 bg-primary/10 text-primary"
                              : "border-border text-muted-foreground hover:border-border/80 hover:text-foreground",
                            !isOwnerOrMember && "cursor-not-allowed opacity-60",
                          )}
                          onClick={() => {
                            setCustomTagSelections((prev) => {
                              const current = prev[group.slug] ?? [];
                              const next = selected
                                ? current.filter((v: string) => v !== opt.value)
                                : [...current, opt.value];
                              return { ...prev, [group.slug]: next };
                            });
                          }}
                        >
                          <div
                            className={cn(
                              "flex size-3.5 shrink-0 items-center justify-center rounded-sm border transition",
                              selected
                                ? "border-primary bg-primary text-primary-foreground"
                                : "border-border",
                            )}
                          >
                            {selected && (
                              <svg
                                className="size-2.5"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="3"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            )}
                          </div>
                          {opt.label}
                        </button>
                      );
                    },
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </JobSettingsSection>
    </div>
  );
}

// ---------------------------------------------------------------------------
// History Panel
// ---------------------------------------------------------------------------

const CHANGE_LOG_ACTION_LABELS: Record<string, string> = {
  created: "Job created",
  converted_to_appointment: "Converted to appointment",
  appointment_changed: "Change of appointment recorded",
  did_not_proceed: "Marked as did not proceed",
  status_changed: "Status changed",
  phase_changed: "Phase changed",
  field_changed: "Details edited",
  members_added: "Members added",
  role_assignment_changed: "Workflow role assignment changed",
  notice_of_appointment_uploaded: "Notice of Appointment uploaded",
  search_recommended: "Search recommended",
  search_approved: "Search approved",
  search_rejected: "Search rejected",
  search_completed: "Search completed",
  search_failed: "Search failed",
  flag_dismissed: "Flag dismissed",
  flag_restored: "Flag restored",
  overview_summary_edited: "Background edited",
  overview_summary_regenerated: "Background regenerated by agent",
};

interface ChangeLogActor {
  firstName?: string;
  lastName?: string;
  email: string;
  avatarUrl?: string;
}

interface ChangeLogEntryData {
  _id: Id<"jobChangeLog">;
  _creationTime: number;
  action: string;
  message?: string;
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
  actor?: ChangeLogActor;
}

/**
 * Renders the change-log entries for the job, plus the "Did Not Proceed"
 * action for pre-appointment jobs (terminal close-out for lost pitches).
 */
function JobHistoryPanel({
  jobId,
  isPreAppointment,
  userRole,
}: {
  jobId: Id<"jobs">;
  isPreAppointment: boolean;
  userRole: JobRole;
}) {
  const [didNotProceedOpen, setDidNotProceedOpen] = useState(false);

  const { data: entries, isLoading } = useQuery(
    convexQuery(api.jobs.queries.listJobChangeLog, { jobId, limit: 200 }),
  );

  const canCloseAsLost =
    isPreAppointment && (userRole === "owner" || userRole === "member");

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h2 className="text-lg font-semibold tracking-tight">History</h2>
          <p className="text-sm text-muted-foreground">
            Audit trail of changes to this job — creation, conversions, status
            transitions, and key field edits.
          </p>
        </div>
        {canCloseAsLost && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-1.5 text-destructive hover:text-destructive"
            onClick={() => setDidNotProceedOpen(true)}
          >
            <AlertCircle className="h-3.5 w-3.5" />
            Mark as Did Not Proceed
          </Button>
        )}
      </div>

      <DidNotProceedDialog
        jobId={jobId}
        open={didNotProceedOpen}
        onOpenChange={setDidNotProceedOpen}
      />

      {isLoading ? (
        <div className="flex h-32 items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : !entries || entries.length === 0 ? (
        <div className="rounded-md border border-dashed bg-muted/20 px-5 py-8 text-center">
          <p className="text-sm text-muted-foreground">
            No history entries yet.
          </p>
        </div>
      ) : (
        <ol className="flex flex-col gap-3">
          {entries.map((entry) => (
            <ChangeLogEntry key={entry._id} entry={entry} />
          ))}
        </ol>
      )}
    </div>
  );
}

function ChangeLogEntry({ entry }: { entry: ChangeLogEntryData }) {
  const label =
    CHANGE_LOG_ACTION_LABELS[entry.action] ?? humanizeAction(entry.action);

  const actorName = entry.actor
    ? entry.actor.firstName || entry.actor.lastName
      ? [entry.actor.firstName, entry.actor.lastName].filter(Boolean).join(" ")
      : entry.actor.email
    : "System";

  const initials =
    actorName
      .split(" ")
      .map((part) => part[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase() || "S";

  return (
    <li className="flex items-start gap-3 rounded-lg border bg-background px-4 py-3">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted">
        {entry.actor?.avatarUrl ? (
          <img
            src={entry.actor.avatarUrl}
            alt={actorName}
            className="h-8 w-8 rounded-full object-cover"
          />
        ) : (
          <span className="text-xs font-medium text-muted-foreground">
            {initials}
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="text-sm font-medium">{label}</span>
          <span className="text-xs text-muted-foreground">by {actorName}</span>
          <span className="text-xs text-muted-foreground">
            {formatHistoryTimestamp(entry._creationTime)}
          </span>
        </div>
        {entry.message && (
          <p className="text-xs text-muted-foreground">{entry.message}</p>
        )}
        {entry.before && entry.after ? (
          <ChangeDiff before={entry.before} after={entry.after} />
        ) : entry.after ? (
          <ChangeAfterOnly after={entry.after} />
        ) : null}
      </div>
    </li>
  );
}

function ChangeDiff({
  before,
  after,
}: {
  before: Record<string, unknown>;
  after: Record<string, unknown>;
}) {
  const fields = Object.keys(after);
  if (fields.length === 0) return null;
  return (
    <ul className="mt-1 flex flex-col gap-0.5 text-xs">
      {fields.map((field) => (
        <li key={field} className="flex flex-wrap items-baseline gap-x-1.5">
          <span className="font-medium">{humanizeField(field)}:</span>
          <span className="text-muted-foreground line-through">
            {formatChangeValue(before[field])}
          </span>
          <span className="text-muted-foreground">→</span>
          <span>{formatChangeValue(after[field])}</span>
        </li>
      ))}
    </ul>
  );
}

function ChangeAfterOnly({ after }: { after: Record<string, unknown> }) {
  const fields = Object.keys(after);
  if (fields.length === 0) return null;
  return (
    <ul className="mt-1 flex flex-col gap-0.5 text-xs text-muted-foreground">
      {fields.map((field) => (
        <li key={field}>
          <span className="font-medium">{humanizeField(field)}:</span>{" "}
          {formatChangeValue(after[field])}
        </li>
      ))}
    </ul>
  );
}

function humanizeAction(raw: string): string {
  return raw
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function humanizeField(raw: string): string {
  return raw
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

function formatChangeValue(value: unknown): string {
  if (value === undefined || value === null || value === "") return "—";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean")
    return String(value);
  if (Array.isArray(value)) return value.length === 0 ? "[]" : value.join(", ");
  return JSON.stringify(value);
}

function formatHistoryTimestamp(ts: number): string {
  const date = new Date(ts);
  return date.toLocaleString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Confirmation dialog for marking a pre-appointment job as
 * `did_not_proceed`. Captures an optional reason that gets stored in
 * `jobs.lostReason` and surfaced in the change log.
 */
function DidNotProceedDialog({
  jobId,
  open,
  onOpenChange,
}: {
  jobId: Id<"jobs">;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [reason, setReason] = useState("");
  const { mutateAsync: markJobDidNotProceed, isPending } = useMutation({
    mutationFn: useConvexMutation(api.jobs.mutations.markJobDidNotProceed),
  });

  useEffect(() => {
    if (open) setReason("");
  }, [open]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await markJobDidNotProceed({
        jobId,
        reason: reason.trim() || undefined,
      });
      toast.success("Job marked as did not proceed");
      onOpenChange(false);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to update job";
      toast.error(message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Mark as Did Not Proceed</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <p className="text-sm text-muted-foreground">
            This marks the job as a lost pitch. The status flips to{" "}
            <span className="font-medium">Did Not Proceed</span> and the job
            becomes read-only. This action can&rsquo;t be undone in v1.
          </p>
          <div className="flex flex-col gap-2">
            <Label htmlFor="lostReason">Reason (optional)</Label>
            <Input
              id="lostReason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Conflict could not be managed"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button type="submit" variant="destructive" disabled={isPending}>
              {isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Updating…
                </>
              ) : (
                "Mark as Did Not Proceed"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
